/** Lazy-instantiation factory for player skill effects. */
import { Player } from '../entities/Player';
import { Enemy } from '../entities/Enemy';
import { EnemyManager } from '../systems/EnemyManager';
import { UpgradeEffectResolver } from '../systems/UpgradeEffectResolver';
import { ConfigLoader } from '../systems/ConfigLoader';
import { calculateDanger } from '../systems/DangerCalculator';
import { TILE_SIZE } from '../utils/Constants';
import { tileBlocksMovement } from '../ai/constants';
import { KnockbackSystem } from '../systems/KnockbackSystem';
import { HideSkillEffect } from '../skills/player/HideSkillEffect';
import { StealthSkillEffect } from '../skills/player/StealthSkillEffect';
import { PunchSkillEffect } from '../skills/player/PunchSkillEffect';
import { PunchmoreSkillEffect } from '../skills/player/PunchmoreSkillEffect';
// Grit tree
import { ForceDriverSkillEffect } from '../skills/player/grit/ForceDriverSkillEffect';
import { ShatterpointSkillEffect } from '../skills/player/grit/ShatterpointSkillEffect';
import { PummelSkillEffect } from '../skills/player/grit/PummelSkillEffect';
import { HaymakerSkillEffect } from '../skills/player/grit/HaymakerSkillEffect';
import { TremorPalmSkillEffect } from '../skills/player/grit/TremorPalmSkillEffect';
import { GrappleSkillEffect } from '../skills/player/grit/GrappleSkillEffect';
import { ForceBindSkillEffect } from '../skills/player/grit/ForceBindSkillEffect';
import { CrushSkillEffect } from '../skills/player/grit/CrushSkillEffect';
import { DeadWeightSkillEffect } from '../skills/player/grit/DeadWeightSkillEffect';
import { ForceSkillEffect } from '../skills/player/grit/ForceSkillEffect';
import { IronbodySkillEffect } from '../skills/player/grit/IronbodySkillEffect';
import { IronbodyMk2SkillEffect } from '../skills/player/grit/IronbodyMk2SkillEffect';
import { TitanSkillEffect } from '../skills/player/grit/TitanSkillEffect';
import { GolemSkillEffect } from '../skills/player/grit/GolemSkillEffect';
import { WeightOfTheWorldSkillEffect } from '../skills/player/grit/WeightOfTheWorldSkillEffect';
import { SkillEffect } from '../skills/SkillEffect';
// Blades tree
import { StabSkillEffect } from '../skills/player/blades/StabSkillEffect';
import { QuickbladeSkillEffect } from '../skills/player/blades/QuickbladeSkillEffect';
import { SlashSkillEffect } from '../skills/player/blades/SlashSkillEffect';
import { FlurrySkillEffect } from '../skills/player/blades/FlurrySkillEffect';
import { RiposteSkillEffect } from '../skills/player/blades/RiposteSkillEffect';
import { StormRunSkillEffect } from '../skills/player/blades/StormRunSkillEffect';
import { StormOfBladesSkillEffect } from '../skills/player/blades/StormOfBladesSkillEffect';
import { RendSkillEffect } from '../skills/player/blades/RendSkillEffect';
import { SeverSkillEffect } from '../skills/player/blades/SeverSkillEffect';
import { BleedSkillEffect } from '../skills/player/blades/BleedSkillEffect';
import { HamstringSkillEffect } from '../skills/player/blades/HamstringSkillEffect';
import { OpenWoundSkillEffect } from '../skills/player/blades/OpenWoundSkillEffect';
import { SunderSkillEffect } from '../skills/player/blades/SunderSkillEffect';
import { RuinSkillEffect } from '../skills/player/blades/RuinSkillEffect';
import { SurgicalStrikeSkillEffect } from '../skills/player/blades/SurgicalStrikeSkillEffect';
import { PhaseMarkSkillEffect } from '../skills/player/blades/PhaseMarkSkillEffect';
import { PhaseBladeSkillEffect } from '../skills/player/blades/PhaseBladeSkillEffect';
// Specter tree
import { PhantomSkillEffect } from '../skills/player/specter/PhantomSkillEffect';
import { DoppelgangerSkillEffect } from '../skills/player/specter/DoppelgangerSkillEffect';
import { OrchestrateSkillEffect } from '../skills/player/specter/OrchestrateSkillEffect';
import { PuppeteerSkillEffect } from '../skills/player/specter/PuppeteerSkillEffect';
import { VeilSkillEffect } from '../skills/player/specter/VeilSkillEffect';
import { MirageSkillEffect } from '../skills/player/specter/MirageSkillEffect';
import { BroadcastSkillEffect } from '../skills/player/specter/BroadcastSkillEffect';
import { DistortionSkillEffect } from '../skills/player/specter/DistortionSkillEffect';
import { TransitSkillEffect } from '../skills/player/specter/TransitSkillEffect';
import { DreadSovereignSkillEffect } from '../skills/player/specter/DreadSovereignSkillEffect';
import { WraithSkillEffect } from '../skills/player/specter/WraithSkillEffect';
import { FlickerSkillEffect } from '../skills/player/specter/FlickerSkillEffect';
import { FlowStateSkillEffect } from '../skills/player/specter/FlowStateSkillEffect';
import { SurgeSkillEffect } from '../skills/player/specter/SurgeSkillEffect';
import { DeepReservesSkillEffect } from '../skills/player/specter/DeepReservesSkillEffect';
import { PerpetualMotionSkillEffect } from '../skills/player/specter/PerpetualMotionSkillEffect';
import { AdrenalineSkillEffect } from '../skills/player/specter/AdrenalineSkillEffect';
import { BlinkSkillEffect as SpecterBlinkSkillEffect } from '../skills/player/specter/BlinkSkillEffect';
import { GhostDashSkillEffect } from '../skills/player/specter/GhostDashSkillEffect';
import { PhaseDashSkillEffect } from '../skills/player/specter/PhaseDashSkillEffect';
import { ExtraPipSkillEffect } from '../skills/player/specter/ExtraPipSkillEffect';
import { SlipstreamSkillEffect } from '../skills/player/specter/SlipstreamSkillEffect';
import { SteadyPaceSkillEffect } from '../skills/player/specter/SteadyPaceSkillEffect';
import { QuickRecoverySkillEffect } from '../skills/player/specter/QuickRecoverySkillEffect';
import { TirelessSkillEffect } from '../skills/player/specter/TirelessSkillEffect';
import { SanctuarySkillEffect } from '../skills/player/specter/SanctuarySkillEffect';
// Fire tree
import { LighterSkillEffect } from '../skills/player/fire/LighterSkillEffect';
import { ConduitSkillEffect } from '../skills/player/fire/ConduitSkillEffect';
import { VasFlamSkillEffect } from '../skills/player/fire/VasFlamSkillEffect';
import { ConflagrationSkillEffect } from '../skills/player/fire/ConflagrationSkillEffect';
import { BackdraftSkillEffect } from '../skills/player/fire/BackdraftSkillEffect';
import { FlashpointSkillEffect } from '../skills/player/fire/FlashpointSkillEffect';
import { EmberSkillEffect } from '../skills/player/fire/EmberSkillEffect';
import { WarmthSkillEffect } from '../skills/player/fire/WarmthSkillEffect';
import { CultivateSkillEffect } from '../skills/player/fire/CultivateSkillEffect';
import { ShapeSkillEffect } from '../skills/player/fire/ShapeSkillEffect';
import { ChokeSkillEffect } from '../skills/player/fire/ChokeSkillEffect';
import { CauterizeSkillEffect } from '../skills/player/fire/CauterizeSkillEffect';
import { TemperSkillEffect } from '../skills/player/fire/TemperSkillEffect';
import { BonfireSkillEffect } from '../skills/player/fire/BonfireSkillEffect';
import { ImmolateSkillEffect } from '../skills/player/fire/ImmolateSkillEffect';
import { AshfallSkillEffect } from '../skills/player/fire/AshfallSkillEffect';
import { HearthfireSkillEffect } from '../skills/player/fire/HearthfireSkillEffect';
// Poison tree
import { TaintSkillEffect } from '../skills/player/poison/TaintSkillEffect';
import { VirulentTouchSkillEffect } from '../skills/player/poison/VirulentTouchSkillEffect';
import { CorrodeSkillEffect } from '../skills/player/poison/CorrodSkillEffect';
import { MiasmaSkillEffect } from '../skills/player/poison/MiasmaSkillEffect';
import { HazeSkillEffect } from '../skills/player/poison/HazeSkillEffect';
import { AcidRainSkillEffect } from '../skills/player/poison/AcidRainSkillEffect';
import { BlightSkillEffect } from '../skills/player/poison/BlightSkillEffect';
import { PlagueVectorSkillEffect } from '../skills/player/poison/PlagueVectorSkillEffect';
import { ContagionSkillEffect } from '../skills/player/poison/ContagionSkillEffect';
import { CarrierPassiveSkillEffect } from '../skills/player/poison/CarrierPassiveSkillEffect';
import { ChainSkillEffect } from '../skills/player/poison/ChainSkillEffect';
import { VectorsPassiveSkillEffect } from '../skills/player/poison/VectorsPassiveSkillEffect';
import { CharnelGroundSkillEffect } from '../skills/player/poison/CharnelGroundSkillEffect';
import { EntropySkillEffect } from '../skills/player/poison/EntropySkillEffect';
import { ExpelSkillEffect } from '../skills/player/poison/ExpelSkillEffect';
import { VenombloodSkillEffect } from '../skills/player/poison/VenombloodSkillEffect';
import { ExpelMkIISkillEffect } from '../skills/player/poison/ExpelMkIISkillEffect';
import { AbsorptionPassiveSkillEffect } from '../skills/player/poison/AbsorptionPassiveSkillEffect';
import { EcdysisSkillEffect } from '../skills/player/poison/EcdysisSkillEffect';
import { ResiduePassiveSkillEffect } from '../skills/player/poison/ResiduePassiveSkillEffect';
import { CorpseBloomSkillEffect } from '../skills/player/poison/CorpseBloomSkillEffect';
import { VirulentRemainsPassiveSkillEffect } from '../skills/player/poison/VirulentRemainsPassiveSkillEffect';
import { SynthesisSkillEffect } from '../skills/player/poison/SynthesisSkillEffect';
// Earth tree
import { StompSkillEffect } from '../skills/player/earth/StompSkillEffect';
import { QuakeSkillEffect } from '../skills/player/earth/QuakeSkillEffect';
import { BedrockSkillEffect } from '../skills/player/earth/BedrockSkillEffect';
import { TremorSenseSkillEffect } from '../skills/player/earth/TremorSenseSkillEffect';
import { StoneFistSkillEffect } from '../skills/player/earth/StoneFistSkillEffect';
import { HammerSkillEffect } from '../skills/player/earth/HammerSkillEffect';
import { AftershockSkillEffect } from '../skills/player/earth/AftershockSkillEffect';
import { WallSkillEffect } from '../skills/player/earth/WallSkillEffect';
import { RampartSkillEffect } from '../skills/player/earth/RampartSkillEffect';
import { DeepRootSkillEffect } from '../skills/player/earth/DeepRootSkillEffect';
import { SculptorSkillEffect } from '../skills/player/earth/SculptorSkillEffect';
import { PillarSkillEffect } from '../skills/player/earth/PillarSkillEffect';
import { OvergrownSkillEffect } from '../skills/player/earth/OvergrownSkillEffect';
import { RuptureSkillEffect } from '../skills/player/earth/RuptureSkillEffect';
import { CraterDropSkillEffect } from '../skills/player/earth/CraterDropSkillEffect';
import { IronThresholdSkillEffect } from '../skills/player/earth/IronThresholdSkillEffect';
import { StandingWaveSkillEffect } from '../skills/player/earth/StandingWaveSkillEffect';
import { FortifySkillEffect, FortifyPlayerState } from '../skills/player/earth/FortifySkillEffect';
import { WeightBearingSkillEffect } from '../skills/player/earth/WeightBearingSkillEffect';
import { CairnSkillEffect } from '../skills/player/earth/CairnSkillEffect';
import { SinkholeSkillEffect } from '../skills/player/earth/SinkholeSkillEffect';
import { TerraformSkillEffect } from '../skills/player/earth/TerraformSkillEffect';
import { MonolithSkillEffect } from '../skills/player/earth/MonolithSkillEffect';
import { PetrifySkillEffect } from '../skills/player/earth/PetrifySkillEffect';
// Lightning tree
import { SparkSkillEffect } from '../skills/player/lightning/SparkSkillEffect';
import { JoltSkillEffect } from '../skills/player/lightning/JoltSkillEffect';
import { StaticBuildupSkillEffect } from '../skills/player/lightning/StaticBuildupSkillEffect';
import { GatheringStormSkillEffect } from '../skills/player/lightning/GatheringStormSkillEffect';
import { ArcSkillEffect, ArcChainResult } from '../skills/player/lightning/ArcSkillEffect';
import { ChainLightningSkillEffect, ChainLightningChainResult } from '../skills/player/lightning/ChainLightningSkillEffect';
import { BoltSkillEffect } from '../skills/player/lightning/BoltSkillEffect';
import { StormcallerSkillEffect } from '../skills/player/lightning/StormcallerSkillEffect';
import { TempestSkillEffect, TempestChainResult } from '../skills/player/lightning/TempestSkillEffect';
import { PulseSkillEffect } from '../skills/player/lightning/PulseSkillEffect';
import { SeizureSkillEffect } from '../skills/player/lightning/SeizureSkillEffect';
import { SeizeSkillEffect } from '../skills/player/lightning/SeizeSkillEffect';
import { DeadZoneSkillEffect } from '../skills/player/lightning/DeadZoneSkillEffect';
import { HijackSkillEffect } from '../skills/player/lightning/HijackSkillEffect';
import { DischargeSkillEffect } from '../skills/player/lightning/DischargeSkillEffect';
import { OverloadSkillEffect } from '../skills/player/lightning/OverloadSkillEffect';
import { LightningReflexesSkillEffect } from '../skills/player/lightning/LightningReflexesSkillEffect';
import { ArcStormSkillEffect } from '../skills/player/lightning/ArcStormSkillEffect';
import { BecomesTheStormSkillEffect } from '../skills/player/lightning/BecomesTheStormSkillEffect';
// Conjuration tree
import { SummonSkillEffect } from '../skills/player/conjuration/SummonSkillEffect';
import { InvokeSkillEffect } from '../skills/player/conjuration/InvokeSkillEffect';
import { BondSkillEffect } from '../skills/player/conjuration/BondSkillEffect';
import { VestigeSkillEffect } from '../skills/player/conjuration/VestigeSkillEffect';
import { SwarmSkillEffect } from '../skills/player/conjuration/SwarmSkillEffect';
import { OverwhelmSkillEffect } from '../skills/player/conjuration/OverwhelmSkillEffect';
import { ExpendableSkillEffect } from '../skills/player/conjuration/ExpendableSkillEffect';
import { DetonateSkillEffect } from '../skills/player/conjuration/DetonateSkillEffect';
import { ChainDetonationSkillEffect } from '../skills/player/conjuration/ChainDetonationSkillEffect';
import { HairTriggerSkillEffect } from '../skills/player/conjuration/HairTriggerSkillEffect';
import { ConvergenceSkillEffect } from '../skills/player/conjuration/ConvergenceSkillEffect';
import { RelentlessSkillEffect } from '../skills/player/conjuration/RelentlessSkillEffect';
import { BindSkillEffect } from '../skills/player/conjuration/BindSkillEffect';
import { EmpowerSkillEffect } from '../skills/player/conjuration/EmpowerSkillEffect';
import { DevotedSkillEffect } from '../skills/player/conjuration/DevotedSkillEffect';
import { ChimeraSkillEffect } from '../skills/player/conjuration/ChimeraSkillEffect';
import { BondedSkillEffect } from '../skills/player/conjuration/BondedSkillEffect';
import { RevivalSkillEffect } from '../skills/player/conjuration/RevivalSkillEffect';
import { TetherSkillEffect } from '../skills/player/conjuration/TetherSkillEffect';
import { ClaritySkillEffect } from '../skills/player/conjuration/ClaritySkillEffect';
import { SynapseSkillEffect } from '../skills/player/conjuration/SynapseSkillEffect';
import { MirrorSkillEffect } from '../skills/player/conjuration/MirrorSkillEffect';
import { RecursiveSkillEffect } from '../skills/player/conjuration/RecursiveSkillEffect';
import { FractureSkillEffect } from '../skills/player/conjuration/FractureSkillEffect';
import { ResonantFormationSkillEffect } from '../skills/player/conjuration/ResonantFormationSkillEffect';
import { UnmooredSkillEffect } from '../skills/player/conjuration/UnmooredSkillEffect';
import { DetachSkillEffect } from '../skills/player/conjuration/DetachSkillEffect';
import { SeveranceSkillEffect } from '../skills/player/conjuration/SeveranceSkillEffect';
import { DetachBarrierPassiveSkillEffect } from '../skills/player/conjuration/DetachBarrierPassiveSkillEffect';
import { BecomesTheManySkillEffect } from '../skills/player/conjuration/BecomesTheManySkillEffect';
import { ManifestSkillEffect } from '../skills/player/conjuration/ManifestSkillEffect';
import { FractureApotheosisSkillEffect } from '../skills/player/conjuration/FractureApotheosisSkillEffect';
import { AnchoredSkillEffect } from '../skills/player/conjuration/AnchoredSkillEffect';
import { StatusEffectSystem } from '../systems/StatusEffectSystem';
import type { SkillEffectDeclarationEngine, ResolverContext, ResolvedEffect } from '../systems/SkillEffectDeclarationEngine';
import type { EffectTrigger } from '../utils/Types';
import { ZoneSystem } from '../systems/ZoneSystem';
import { SummonSystem, SummonConfig } from '../systems/SummonSystem';
import type { WispData } from '../skills/player/conjuration/DetonateSkillEffect';
import { DeathTriggerSystem } from '../systems/DeathTriggerSystem';
import { ChainTargetingSystem } from '../systems/ChainTargetingSystem';
import { CooldownHookSystem } from '../systems/CooldownHookSystem';
import { ChargeSystem } from '../systems/ChargeSystem';
import { StationaryTracker } from '../systems/StationaryTracker';
import { TerrainConstructionSystem } from '../systems/TerrainConstructionSystem';
import { PlayerProjectilePool } from '../systems/PlayerProjectilePool';
import { AstralTeleportSystem } from '../systems/AstralTeleportSystem';
import { ForesightRenderSystem } from '../systems/ForesightRenderSystem';
import { GravityPullSystem } from '../systems/GravityPullSystem';
import { OrbitalDebrisSystem } from '../systems/OrbitalDebrisSystem';
import { TemporalEchoStateSystem } from '../systems/TemporalEchoStateSystem';
import { AstralProjectionStateSystem } from '../systems/AstralProjectionStateSystem';
// Astral tree
import { BlinkSkillEffect } from '../skills/player/astral/BlinkSkillEffect';
import { PhaseStepSkillEffect } from '../skills/player/astral/PhaseStepSkillEffect';
import { SpatialMemorySkillEffect } from '../skills/player/astral/SpatialMemorySkillEffect';
import { ForesightPassiveSkillEffect } from '../skills/player/astral/ForesightPassiveSkillEffect';
import { RiftSkillEffect } from '../skills/player/astral/RiftSkillEffect';
import { ImplosionSkillEffect } from '../skills/player/astral/ImplosionSkillEffect';
import { FoldedSpacePassiveSkillEffect } from '../skills/player/astral/FoldedSpacePassiveSkillEffect';
import { EventHorizonSkillEffect } from '../skills/player/astral/EventHorizonSkillEffect';
import { SingularityPassiveSkillEffect } from '../skills/player/astral/SingularityPassiveSkillEffect';
import { AnchorSkillEffect } from '../skills/player/astral/AnchorSkillEffect';
import { WarpGeometryPassiveSkillEffect } from '../skills/player/astral/WarpGeometryPassiveSkillEffect';
import { OracleSkillEffect } from '../skills/player/astral/OracleSkillEffect';
import { PrescienceSkillEffect } from '../skills/player/astral/PrescienceSkillEffect';
import { SecondSightPassiveSkillEffect } from '../skills/player/astral/SecondSightPassiveSkillEffect';
import { AstralProjectionSkillEffect } from '../skills/player/astral/AstralProjectionSkillEffect';
import { HauntedPassiveSkillEffect } from '../skills/player/astral/HauntedPassiveSkillEffect';
import { TemporalEchoSkillEffect } from '../skills/player/astral/TemporalEchoSkillEffect';
import { ForeshadowPassiveSkillEffect } from '../skills/player/astral/ForeshadowPassiveSkillEffect';
import { DenseSkillEffect } from '../skills/player/astral/DenseSkillEffect';
import { CollapseSkillEffect } from '../skills/player/astral/CollapseSkillEffect';
import { OrbitalDebrisPassiveSkillEffect } from '../skills/player/astral/OrbitalDebrisPassiveSkillEffect';
import { MeteoSkillEffect } from '../skills/player/astral/MeteoSkillEffect';
import { GravityScarPassiveSkillEffect } from '../skills/player/astral/GravityScarPassiveSkillEffect';
import { SlingshotSkillEffect } from '../skills/player/astral/SlingshotSkillEffect';
import { OrbitalBombardmentSkillEffect } from '../skills/player/astral/OrbitalBombardmentSkillEffect';
import { DeadStarSkillEffect } from '../skills/player/astral/DeadStarSkillEffect';
import { GhostProtocolSkillEffect } from '../skills/player/astral/GhostProtocolSkillEffect';
import { OrbitalMechanicsPassiveSkillEffect } from '../skills/player/astral/OrbitalMechanicsPassiveSkillEffect';
import { MassDifferentialPassiveSkillEffect } from '../skills/player/astral/MassDifferentialPassiveSkillEffect';
import { SustainedMassPassiveSkillEffect } from '../skills/player/astral/SustainedMassPassiveSkillEffect';
import { PreservedStateSkillEffect } from '../skills/player/astral/PreservedStateSkillEffect';

/** Everything a skill effect might need from the scene. */
export interface SkillEffectDeps {
  player: Player;
  enemyManager: EnemyManager;
  upgradeResolver: UpgradeEffectResolver;
  cameras: Phaser.Cameras.Scene2D.CameraManager;
  // Shared attunement systems (ADR-064)
  statusEffectSystem: StatusEffectSystem;
  zoneSystem: ZoneSystem;
  summonSystem: SummonSystem;
  deathTriggerSystem: DeathTriggerSystem;
  chainTargetingSystem: ChainTargetingSystem;
  cooldownHookSystem: CooldownHookSystem;
  chargeSystem: ChargeSystem;
  stationaryTracker: StationaryTracker;
  terrainConstructionSystem: TerrainConstructionSystem;
  playerProjectilePool: PlayerProjectilePool;
  knockbackSystem: KnockbackSystem;
  // Astral-specific systems
  astralTeleportSystem: AstralTeleportSystem;
  foresightRenderSystem: ForesightRenderSystem;
  gravityPullSystem: GravityPullSystem;
  orbitalDebrisSystem: OrbitalDebrisSystem;
  temporalEchoStateSystem: TemporalEchoStateSystem;
  astralProjectionStateSystem: AstralProjectionStateSystem;
  groundLayer: Phaser.Tilemaps.TilemapLayer;
  // Declarative skill effect engine (ADR-087)
  declarationEngine: SkillEffectDeclarationEngine;
  // Display hooks for geometric (non-hitbox) damage — floaties and combat crawl
  damageNumbers?: { spawn(worldX: number, worldY: number, damage: number, baseDamage: number, isCrit: boolean, isBackstab: boolean): void } | null;
  pushCrawl?: ((text: string, color: string) => void) | null;
}

/**
 * Lazy-instantiation factory for player skill effects. Foundation skills are created at
 * construction; all others are created on demand when their node fires `node-unlocked`.
 *
 * Phase D wiring registers additional skills via `factory.register(nodeId, factory)`.
 * The lazy creation machinery fires automatically — no changes to this class are needed.
 *
 * Attunement changes (unattune, deselect tree, evolution replace) trigger `_listenForAttunementChanges()`
 * which diffs the attunement set and unregisters removed skill effects / re-registers added ones.
 */
/** Subset of AI adapter query methods exposed for simulation testing. */
export interface FactoryAIQueries {
  getEnemiesInDetectionRange(targetX: number, targetY: number): string[];
  getNearbyEnemies(sourceEnemyId: string, radiusPx: number): string[];
  getNearbyNonInvestigating(sourceEnemyId: string, rangeTiles: number): string[];
}

/** Module-level display hooks, set once by SkillFactory constructor. */
let _damageDisplay: {
  damageNumbers: { spawn(worldX: number, worldY: number, damage: number, baseDamage: number, isCrit: boolean, isBackstab: boolean): void } | null;
  pushCrawl: ((text: string, color: string) => void) | null;
} | null = null;

/** Apply damage to an enemy, scaling by any active vulnerability/armor debuffs. Fires floaties and crawl if display hooks are wired. */
function dealDamageToEnemy(enemy: Enemy, damage: number): void {
  const finalDamage = Math.round(damage * enemy.getVulnerabilityMultiplier());
  enemy.healthComponent.takeDamage(finalDamage);
  _damageDisplay?.damageNumbers?.spawn(enemy.sprite.x, enemy.sprite.y, finalDamage, damage, false, false);
  _damageDisplay?.pushCrawl?.(`Hit ${enemy.config.id} for ${finalDamage} dmg`, '#cccccc');
}

export class SkillFactory {
  private readonly deps: SkillEffectDeps;
  private readonly registry = new Map<string, () => SkillEffect | null>();
  readonly getDanger: () => number;
  /** AI adapter query methods — exposed for simulation bridge testing. */
  private _aiQueries: FactoryAIQueries | null = null;
  get aiQueries(): FactoryAIQueries | null { return this._aiQueries; }

  /** Tracks previously-attuned skill IDs so we can diff on attunement-changed. */
  private _previousAttuned: Set<string> = new Set();
  /** Tracks previously-selected trees so we can diff on tree-selection-changed. */
  private _previousSelectedTrees: Set<string> = new Set();

  constructor(deps: SkillEffectDeps) {
    this.deps = deps;
    _damageDisplay = {
      damageNumbers: deps.damageNumbers ?? null,
      pushCrawl: deps.pushCrawl ?? null,
    };
    this.getDanger = this._buildDangerFn();
    this._buildRegistry();
    this._initialize();
    this._listenForUnlocks();
    this._listenForAttunementChanges();
    this._listenForTreeSelectionChanges();
  }

  /**
   * Register a factory function for a node ID. Called during construction for built-in
   * skills, and by Phase D wiring for tree-specific skill effects.
   * The factory returns the primary SkillEffect instance (or null for no-op entries).
   */
  register(nodeId: string, factory: () => SkillEffect | null): void {
    this.registry.set(nodeId, factory);
  }

  // ── Declarative effect dispatch (ADR-087) ──────────────────────────────────

  /**
   * Resolve and apply all declarative effects for a skill + trigger.
   * Returns the resolved effects that were applied (empty if none matched or
   * the skill has no declarations). Adapter code can call this from activate/hit
   * handlers to let the engine drive status effect application.
   *
   * @param skillId   The skill requesting dispatch (e.g., 'lighter')
   * @param trigger   The trigger point ('activation', 'hit', 'holdEntry', etc.)
   * @param ctx       Resolver context for evaluating conditions and params
   * @param targetEnemy Optional specific enemy to apply debuffs to (falls back to nearest)
   */
  dispatchDeclarativeEffects(
    skillId: string,
    trigger: EffectTrigger,
    ctx: ResolverContext,
    targetEnemy?: Enemy | null,
  ): ResolvedEffect[] {
    const { declarationEngine, player, enemyManager, statusEffectSystem } = this.deps;
    const resolved = declarationEngine.resolveAll(skillId, trigger, ctx);
    for (const effect of resolved) {
      const def = statusEffectSystem.getDef(effect.effectId);
      if (!def) continue;
      if (effect.target === 'self') {
        player.statusEffectTracker?.apply(effect.effectId, effect.applyOptions);
      } else {
        // Debuff: apply to specified enemy or nearest alive enemy
        const target = targetEnemy ?? this._nearestAliveEnemy(2 * TILE_SIZE);
        if (target) {
          target.statusEffectTracker?.apply(effect.effectId, effect.applyOptions);
        } else {
          player.events.emit('dispatch-no-target', { skillId, effectId: effect.effectId });
        }
      }
    }
    return resolved;
  }

  /**
   * Find the nearest alive enemy within range of the player.
   * Shared utility for declarative dispatch and adapter code.
   */
  private _nearestAliveEnemy(maxRangePx: number): Enemy | null {
    const { player, enemyManager } = this.deps;
    let best: Enemy | null = null;
    let bestDistSq = maxRangePx * maxRangePx;
    const px = player.sprite.x, py = player.sprite.y;
    for (const e of enemyManager.all) {
      if (e.healthComponent.isDead) continue;
      const dx = e.sprite.x - px, dy = e.sprite.y - py;
      const dSq = dx * dx + dy * dy;
      if (dSq < bestDistSq) { bestDistSq = dSq; best = e; }
    }
    return best;
  }

  // ── Private ────────────────────────────────────────────────────────────────

  private _buildDangerFn(): () => number {
    const { enemyManager, cameras } = this.deps;
    const xpConfig = ConfigLoader.xpGainConfig;
    return () => calculateDanger(
      enemyManager.all, cameras.main,
      xpConfig.dangerMaxEnemies, xpConfig.dangerPresenceBase,
      xpConfig.dangerDistanceRadius, xpConfig.dangerDistanceFloor,
      xpConfig.dangerSigmoidSteepness, xpConfig.dangerSigmoidMidpoint,
    );
  }

  private _buildRegistry(): void {
    const { player, enemyManager } = this.deps;
    const getDanger = this.getDanger;

    const isPlayerStationary = () => {
      const vel = player.body.velocity;
      return vel.x * vel.x + vel.y * vel.y < 1;
    };

    // Hide + Stealth — circular pair, always co-instantiated via the 'hide' entry
    this.register('hide', () => {
      let stealthEffect: StealthSkillEffect | undefined;

      const hideEffect = new HideSkillEffect({
        anyEnemyHasLOS: () => enemyManager.anyEnemyHasLOS(),
        isPlayerStationary,
        isStealthActive: () => player.skillActivation.isSkillActive('stealth'),
        getXp: () => player.skillProgression.getTreeXp('specter'),
        getDanger,
        onXpGained: (skillId, options) =>
          player.skillProgression.recordUse(skillId, { ...options, danger: getDanger() }),
        onHideBreak: () => { stealthEffect?.breakStealth(); },
      });

      stealthEffect = new StealthSkillEffect({
        isHideActive: () => hideEffect.isActive(),
        activateHide: () => { hideEffect.forceActivate(); },
        isPlayerStationary,
        getHideCooldown: () => hideEffect.cooldown,
        anyEnemyHasLOS: () => enemyManager.anyEnemyHasLOS(),
        getXp: () => player.skillProgression.getTreeXp('specter'),
        getDanger,
        onXpGained: (skillId, options) =>
          player.skillProgression.recordUse(skillId, { ...options, danger: getDanger() }),
        onStealthBreak: () => { hideEffect.breakHide(); },
        getSpecterXp: () => player.skillProgression.getTreeXp('specter'),
        wipeAggroInRadius: (radiusPx) =>
          enemyManager.wipeAggroInRadius(player.sprite.x, player.sprite.y, radiusPx),
      });

      // Stealth is co-created here — 'stealth' is not registered separately
      player.skillActivation.registerEffect(stealthEffect);
      return hideEffect;
    });
    // 'stealth' intentionally not registered — always created via 'hide'

    const isConcealed = () =>
      player.skillActivation.isSkillActive('hide') ||
      player.skillActivation.isSkillActive('stealth');

    this.register('punch', () => new PunchSkillEffect({
      getCombat: () => player.combat,
      getDash: () => player.dash,
      breakHide: () => {
        player.skillActivation.getEffect<HideSkillEffect>('hide')?.breakHide();
      },
      isConcealed,
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
    }));

    this.register('punchmore', () => new PunchmoreSkillEffect({
      getCombat: () => player.combat,
      getDash: () => player.dash,
      breakHide: () => {
        player.skillActivation.getEffect<HideSkillEffect>('hide')?.breakHide();
      },
      isConcealed,
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
    }));

    this._registerGritSkills();
    this._registerBladesSkills();
    this._registerSpecterSkills();
    this._registerFireSkills();
    this._registerPoisonSkills();
    this._registerLightningSkills();
    this._registerEarthSkills();
    this._registerConjurationSkills();
    this._registerAstralSkills();
  }

  /** Register all Grit tree active/evolution skills. Punch/Punchmore already in _buildRegistry(). */
  private _registerGritSkills(): void {
    const { player, enemyManager, upgradeResolver: resolver, deathTriggerSystem, knockbackSystem } = this.deps;

    // Status-effects adapter — routes to per-entity StatusEffectTracker instances.
    // Grit calling convention: apply(statusId, durationMs, [value|config], sourceSkillId)
    const { statusEffectSystem } = this.deps;
    const nearestAliveEnemy = (maxRangePx: number) => {
      let best: import('../entities/Enemy').Enemy | null = null;
      let bestDistSq = maxRangePx * maxRangePx;
      const px = player.sprite.x, py = player.sprite.y;
      for (const e of enemyManager.all) {
        if (e.healthComponent.isDead) continue;
        const dx = e.sprite.x - px, dy = e.sprite.y - py;
        const dSq = dx * dx + dy * dy;
        if (dSq < bestDistSq) { bestDistSq = dSq; best = e; }
      }
      return best;
    };
    const parseGritArgs = (args: unknown[]) => {
      // Last string arg is sourceSkillId; first number is durationMs
      const sourceSkillId = typeof args[args.length - 1] === 'string'
        ? args[args.length - 1] as string : 'unknown';
      const durationMs = typeof args[0] === 'number' ? args[0] as number : undefined;
      let intensity: number | undefined;
      if (args.length >= 3 && typeof args[1] === 'number') intensity = args[1] as number;
      return { sourceSkillId, durationMs, intensity };
    };
    const statusEffects = {
      apply: (statusId: string, ...args: unknown[]) => {
        const def = statusEffectSystem.getDef(statusId);
        if (!def) return;
        const opts = parseGritArgs(args);
        if (def.category === 'buff') {
          player.statusEffectTracker?.apply(statusId, opts);
        } else {
          const target = nearestAliveEnemy(2 * TILE_SIZE);
          target?.statusEffectTracker?.apply(statusId, opts);
        }
      },
      remove: (statusId: string, ..._args: unknown[]) => {
        const def = statusEffectSystem.getDef(statusId);
        if (!def) return;
        if (def.category === 'buff') {
          player.statusEffectTracker?.remove(statusId);
        } else {
          for (const e of enemyManager.all) {
            if (!e.healthComponent.isDead) e.statusEffectTracker?.remove(statusId);
          }
        }
      },
      has: (statusId: string, ..._args: unknown[]): boolean => {
        const def = statusEffectSystem.getDef(statusId);
        if (!def) return false;
        if (def.category === 'buff') {
          return player.statusEffectTracker?.has(statusId) ?? false;
        }
        const target = nearestAliveEnemy(2 * TILE_SIZE);
        return target?.statusEffectTracker?.has(statusId) ?? false;
      },
    };

    // Shared targeting — checks proximity against live enemies
    const targeting = {
      hasTargetInRange: (rangeTiles?: number) => {
        const range = (rangeTiles ?? 1) * TILE_SIZE;
        return enemyManager.all.some(e => {
          const dx = e.sprite.x - player.sprite.x;
          const dy = e.sprite.y - player.sprite.y;
          return Math.sqrt(dx * dx + dy * dy) <= range;
        });
      },
      shouldFizzle: () => !enemyManager.all.some(e => !e.healthComponent.isDead),
      isTargetAlive: () => enemyManager.all.some(e => !e.healthComponent.isDead),
      getTargetEntityId: () => nearestAliveEnemy(Infinity)?.instanceTag,
      getTargetMaxHp: () => nearestAliveEnemy(Infinity)?.healthComponent.max ?? 0,
    };

    // ── Hurtmore path ─────────────────────────────────────────────────────

    this.register('hurtmore', () => new ForceDriverSkillEffect({
      resolver,
      statusEffects,
    }));

    this.register('hurtmore_evolved', () => new ShatterpointSkillEffect({
      resolver,
      statusEffects,
      deathTriggers: deathTriggerSystem,
    }));

    this.register('pummel', () => new PummelSkillEffect({
      resolver,
    }));

    this.register('haymaker', () => new HaymakerSkillEffect({
      resolver,
      statusEffects,
    }));

    this.register('tremor_palm', () => new TremorPalmSkillEffect({
      resolver,
      statusEffects,
      target: targeting,
      deathTrigger: {
        register: (config) => {
          deathTriggerSystem.register({
            id: config.triggerId,
            sourceSkillId: config.sourceSkillId,
            requireWithinMs: config.requireWithinMs,
            explosion: { damage: config.effect.damage, radiusTiles: config.effect.radiusTiles },
          });
        },
        unregister: (triggerId) => deathTriggerSystem.unregister(triggerId),
      },
    }));

    // ── Grapple path ──────────────────────────────────────────────────────

    this.register('grapple', () => new GrappleSkillEffect({
      resolver,
      statusEffects,
      targeting,
    }));

    this.register('grapple_evolved', () => new ForceBindSkillEffect({
      resolver,
      statusEffects,
      targeting,
    }));

    this.register('crush', () => new CrushSkillEffect({
      resolver,
      statusEffects,
      targeting,
    }));

    this.register('dead_weight', () => new DeadWeightSkillEffect({
      resolver,
      statusEffects,
      grapple: {
        isHoldActive: () => {
          const grappleEffect = player.skillActivation.getEffect<GrappleSkillEffect>('grapple')
            ?? player.skillActivation.getEffect<ForceBindSkillEffect>('grapple_evolved');
          return grappleEffect?.isActive() ?? false;
        },
        cancelHold: () => {
          const grappleEffect = player.skillActivation.getEffect<GrappleSkillEffect>('grapple')
            ?? player.skillActivation.getEffect<ForceBindSkillEffect>('grapple_evolved');
          grappleEffect?.deactivate?.();
        },
      },
      throwTarget: {
        launch: (distanceTiles: number) => {
          // Find nearest alive enemy as the throw target (grapple target)
          const px = player.sprite.x, py = player.sprite.y;
          const target = (() => {
            let best: import('../entities/Enemy').Enemy | null = null;
            let bestDist = TILE_SIZE * 2;
            for (const e of enemyManager.all) {
              if (e.healthComponent.isDead) continue;
              const dx = e.sprite.x - px, dy = e.sprite.y - py;
              const d = Math.sqrt(dx * dx + dy * dy);
              if (d < bestDist) { bestDist = d; best = e; }
            }
            return best;
          })();
          if (!target) return { distanceTraveled: 0, hitWall: false, hitEnemy: false };
          const fd = player.facingDirection;
          const len = Math.sqrt(fd.x * fd.x + fd.y * fd.y) || 1;
          const dir = { x: fd.x / len, y: fd.y / len };
          const distPx = distanceTiles * TILE_SIZE;
          const config = ConfigLoader.combat.knockback;
          knockbackSystem.applyRaw(target, dir, distPx * config.velocityMultiplier, config.durationMs);
          return { distanceTraveled: distanceTiles, hitWall: false, hitEnemy: false };
        },
      },
    }));

    this.register('force', () => new ForceSkillEffect({
      resolver,
      statusEffects,
      target: targeting,
    }));

    // ── Ironbody path ─────────────────────────────────────────────────────

    const ironbodyDeps = {
      resolver,
      statusEffects,
    };

    this.register('ironbody', () => new IronbodySkillEffect(ironbodyDeps));

    this.register('ironbody_evolved', () => new IronbodyMk2SkillEffect(ironbodyDeps));

    this.register('titan', () => new TitanSkillEffect({
      resolver,
      statusEffects,
    }));

    this.register('golem', () => new GolemSkillEffect({
      resolver,
      ironbody: {
        isActive: () =>
          (player.skillActivation.getEffect<IronbodySkillEffect>('ironbody')
            ?? player.skillActivation.getEffect<IronbodyMk2SkillEffect>('ironbody_evolved'))
            ?.isActive() ?? false,
      },
      player: {
        get maxHp() { return player.health.max; },
        heal: (amount: number) => { player.health.heal(amount); },
      },
    }));

    this.register('weight_of_the_world', () => new WeightOfTheWorldSkillEffect({
      resolver,
      statusEffects,
    }));
  }

  /** Register all Blades tree active/evolution/apotheosis skills. */
  private _registerBladesSkills(): void {
    const { player, enemyManager, upgradeResolver: resolver, deathTriggerSystem, cooldownHookSystem, zoneSystem } = this.deps;

    // Status-effects adapter — routes to per-entity StatusEffectTracker instances.
    // Blades calling convention: apply(defId, { sourceSkillId, durationMs, intensity?, targetEntityId? })
    const { statusEffectSystem } = this.deps;
    const findEnemyByTag = (tag: string) =>
      enemyManager.all.find(e => e.instanceTag === tag && !e.healthComponent.isDead) ?? null;
    const nearestAliveEnemy = (maxRangePx: number) => {
      let best: import('../entities/Enemy').Enemy | null = null;
      let bestDistSq = maxRangePx * maxRangePx;
      const px = player.sprite.x, py = player.sprite.y;
      for (const e of enemyManager.all) {
        if (e.healthComponent.isDead) continue;
        const dx = e.sprite.x - px, dy = e.sprite.y - py;
        const dSq = dx * dx + dy * dy;
        if (dSq < bestDistSq) { bestDistSq = dSq; best = e; }
      }
      return best;
    };
    const statusEffects = {
      apply: (defId: string, opts: {
        sourceSkillId?: string; durationMs?: number; intensity?: number; targetEntityId?: string;
      } = {}) => {
        const def = statusEffectSystem.getDef(defId);
        if (!def) return;
        const applyOpts = {
          sourceSkillId: opts.sourceSkillId ?? 'unknown',
          durationMs: opts.durationMs,
          intensity: opts.intensity,
        };
        if (def.category === 'buff') {
          player.statusEffectTracker?.apply(defId, applyOpts);
        } else if (opts.targetEntityId) {
          findEnemyByTag(opts.targetEntityId)?.statusEffectTracker?.apply(defId, applyOpts);
        } else {
          nearestAliveEnemy(2 * TILE_SIZE)?.statusEffectTracker?.apply(defId, applyOpts);
        }
      },
      remove: (defId: string, ..._args: unknown[]) => {
        const def = statusEffectSystem.getDef(defId);
        if (!def) return;
        if (def.category === 'buff') {
          player.statusEffectTracker?.remove(defId);
        } else {
          for (const e of enemyManager.all) {
            if (!e.healthComponent.isDead) e.statusEffectTracker?.remove(defId);
          }
        }
      },
      has: (defId: string, ..._args: unknown[]): boolean => {
        const def = statusEffectSystem.getDef(defId);
        if (!def) return false;
        if (def.category === 'buff') {
          return player.statusEffectTracker?.has(defId) ?? false;
        }
        return nearestAliveEnemy(2 * TILE_SIZE)?.statusEffectTracker?.has(defId) ?? false;
      },
      getStacks: (defId: string): number => {
        const def = statusEffectSystem.getDef(defId);
        if (!def) return 0;
        if (def.category === 'buff') {
          return player.statusEffectTracker?.getStacks(defId) ?? 0;
        }
        return nearestAliveEnemy(2 * TILE_SIZE)?.statusEffectTracker?.getStacks(defId) ?? 0;
      },
    };

    // Combat adapter for Blades tree.
    // startAttack delegates to the player's CombatComponent so hitboxes fire normally.
    // Geometric attack methods find targets by shape and apply damage via takeDamage().
    const ARC_RANGE_PX = 2 * TILE_SIZE;
    const LINE_RANGE_PX = 4 * TILE_SIZE;
    const LINE_HALF_WIDTH_PX = TILE_SIZE;
    const DETACH_RANGE_PX = 6 * TILE_SIZE;

    const combat = {
      // CombatComponent.startAttack returns boolean (started vs buffered).
      // Skills that read the return value as "enemies hit" get 1 on success, 0 on buffer/fail.
      // Actual per-enemy hit count comes from CombatHitResolver when the hitbox resolves.
      startAttack: (attackType: string): number => Number(player.combat.startAttack(attackType)),

      // Arc attack: hit all alive enemies within ARC_RANGE_PX and inside the facing-direction cone.
      startArcAttack: (attackType: string, arcDegrees: number): number => {
        const baseDamage = ConfigLoader.getDamageConfig(attackType).baseDamage;
        const px = player.sprite.x, py = player.sprite.y;
        const fx = player.facingDirection.x, fy = player.facingDirection.y;
        const halfArcCos = Math.cos((arcDegrees / 2) * Math.PI / 180);
        const rangeSq = ARC_RANGE_PX * ARC_RANGE_PX;
        let hits = 0;
        for (const e of enemyManager.all) {
          if (e.healthComponent.isDead) continue;
          const dx = e.sprite.x - px, dy = e.sprite.y - py;
          const dSq = dx * dx + dy * dy;
          if (dSq > rangeSq || dSq === 0) continue;
          const dot = fx * dx + fy * dy;
          if (dot / Math.sqrt(dSq) >= halfArcCos) {
            dealDamageToEnemy(e, baseDamage);
            hits++;
          }
        }
        return hits;
      },

      // Line attack: hit enemies in a narrow corridor along facing direction, sorted by distance.
      startLineAttack: (attackType: string, maxTargets: number): string[] => {
        const baseDamage = ConfigLoader.getDamageConfig(attackType).baseDamage;
        const px = player.sprite.x, py = player.sprite.y;
        const fx = player.facingDirection.x, fy = player.facingDirection.y;
        const candidates: { tag: string; dist: number; enemy: typeof enemyManager.all[0] }[] = [];
        for (const e of enemyManager.all) {
          if (e.healthComponent.isDead) continue;
          const dx = e.sprite.x - px, dy = e.sprite.y - py;
          const along = fx * dx + fy * dy;
          if (along <= 0 || along > LINE_RANGE_PX) continue;
          const cross = Math.abs(fx * dy - fy * dx);
          if (cross <= LINE_HALF_WIDTH_PX) {
            candidates.push({ tag: e.instanceTag, dist: along, enemy: e });
          }
        }
        candidates.sort((a, b) => a.dist - b.dist);
        const targets = candidates.slice(0, maxTargets);
        for (const t of targets) {
          dealDamageToEnemy(t.enemy, baseDamage);
        }
        return targets.map(t => t.tag);
      },

      // Orbital contact: hit all alive enemies within rangeTiles radius each tick.
      applyOrbitalContactDamage: (attackType: string, rangeTiles: number): number => {
        const baseDamage = ConfigLoader.getDamageConfig(attackType).baseDamage;
        const px = player.sprite.x, py = player.sprite.y;
        const rangePx = rangeTiles * TILE_SIZE;
        const rangeSq = rangePx * rangePx;
        let hits = 0;
        for (const e of enemyManager.all) {
          if (e.healthComponent.isDead) continue;
          const dx = e.sprite.x - px, dy = e.sprite.y - py;
          if (dx * dx + dy * dy <= rangeSq) {
            dealDamageToEnemy(e, baseDamage);
            hits++;
          }
        }
        return hits;
      },

      // Detach strike: hit nearest alive enemy within DETACH_RANGE_PX.
      startDetachStrike: (attackType: string): number => {
        const target = nearestAliveEnemy(DETACH_RANGE_PX);
        if (!target) return 0;
        const baseDamage = ConfigLoader.getDamageConfig(attackType).baseDamage;
        dealDamageToEnemy(target, baseDamage);
        return 1;
      },

      // Detonation: apply pre-calculated damage to nearest alive enemy.
      applyDetonationDamage: (_attackType: string, damage: number): void => {
        const target = nearestAliveEnemy(2 * TILE_SIZE);
        if (target && damage > 0) {
          dealDamageToEnemy(target, damage);
        }
      },
    };

    // Concealment adapter for Stab/Quickblade
    const concealment = {
      isConcealed: () =>
        player.skillActivation.isSkillActive('hide') ||
        player.skillActivation.isSkillActive('stealth'),
      breakHide: () => {
        player.skillActivation.getEffect<HideSkillEffect>('hide')?.breakHide();
      },
    };

    // Targeting — resolves nearest alive enemy for Blades skill queries
    const targeting = {
      isRanged: () => {
        const enemy = nearestAliveEnemy(Infinity);
        if (!enemy) return false;
        return enemy.config.skills.some(s => s.type === 'ranged' || s.type === 'bolt');
      },
      getDefenseType: () => 'unarmored' as const, // enemies lack defense-type data; default until system exists
      getCurrentTargetId: () => nearestAliveEnemy(Infinity)?.instanceTag ?? '',
    };

    // STUB: deferred — no passive-state tracker system; Blade Dance stacks gate Storm Run/Storm of Blades
    const bladeDance = { get stacks() { return 0; } };

    // STUB: deferred — no per-target stack tracker; Keen Edge stacks drive Surgical Strike crit bonus
    const keenEdge = {
      getStacks: (_targetId: string) => 0,
      resetToValue: (_targetId: string, _value: number) => { /* no-op */ },
    };

    // Crit stub
    const crit = {
      rollCrit: (_bonusCritChance: number) => Math.random() < _bonusCritChance,
    };

    // ── Trunk ──────────────────────────────────────────────────────────────

    this.register('stab', () => new StabSkillEffect({
      resolver,
      combat,
      concealment,
    }));

    this.register('stab_evolved', () => new QuickbladeSkillEffect({
      resolver,
      combat,
      concealment,
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
    }));

    // ── Slash path ─────────────────────────────────────────────────────────

    this.register('slash', () => new SlashSkillEffect({
      resolver,
      combat,
    }));

    this.register('slash_evolved', () => new FlurrySkillEffect({
      resolver,
      combat,
    }));

    this.register('riposte', () => new RiposteSkillEffect({
      resolver,
      combat,
      statusEffects,
      cooldownHooks: {
        notify: (event: { type: string; conditionId: string }) => {
          cooldownHookSystem.notify({ type: 'onCondition', conditionId: event.conditionId });
        },
      },
    }));

    this.register('storm_run', () => new StormRunSkillEffect({
      resolver,
      combat,
      bladeDance,
      zones: {
        create: (config: unknown): string =>
          zoneSystem.create(config as Parameters<typeof zoneSystem.create>[0]),
      },
    }));

    this.register('storm_of_blades', () => new StormOfBladesSkillEffect({
      resolver,
      bladeDance,
      combat,
    }));

    // ── Rend path ──────────────────────────────────────────────────────────

    this.register('rend', () => new RendSkillEffect({
      resolver,
      combat,
      statusEffects,
    }));

    this.register('rend_evolved', () => new SeverSkillEffect({
      resolver,
      combat,
      statusEffects,
    }));

    this.register('bleed', () => new BleedSkillEffect({
      resolver,
      combat,
      statusEffects,
    }));

    this.register('hamstring', () => new HamstringSkillEffect({
      resolver,
      combat,
      statusEffects,
      target: targeting,
    }));

    this.register('open_wound', () => new OpenWoundSkillEffect({
      statusEffects,
      combat,
    }));

    // ── Sunder path ────────────────────────────────────────────────────────

    this.register('sunder', () => new SunderSkillEffect({
      combat,
      statusEffects,
      target: targeting,
    }));

    this.register('sunder_evolved', () => new RuinSkillEffect({
      combat,
      statusEffects,
      target: targeting,
    }));

    this.register('surgical_strike', () => new SurgicalStrikeSkillEffect({
      resolver,
      combat,
      keenEdge,
      statusEffects,
      target: targeting,
      crit,
    }));

    this.register('phase_mark', () => new PhaseMarkSkillEffect({
      resolver,
      statusEffects,
      cooldownHooks: {
        notifyCondition: (conditionId: string) => {
          cooldownHookSystem.notify({ type: 'onCondition', conditionId });
        },
      },
      target: targeting,
      deathTriggers: {
        register: (config) => { deathTriggerSystem.register(config); },
        unregister: (triggerId) => { deathTriggerSystem.unregister(triggerId); },
      },
    }));

    this.register('phase_blade', () => new PhaseBladeSkillEffect({
      resolver,
    }));
  }

  /** Register all Specter tree active/evolution/apotheosis skills. Hide/Stealth already in _buildRegistry(). */
  private _registerSpecterSkills(): void {
    const { player, enemyManager, upgradeResolver: resolver, summonSystem } = this.deps;

    // Status-effects adapter — routes to per-entity StatusEffectTracker instances.
    // Specter calling convention: apply(defId, { sourceSkillId, durationMs, targetEntityId?, ... })
    // Specter uses targeted remove/resetDuration with explicit entityId args.
    const { statusEffectSystem } = this.deps;
    const findEnemyByTag = (tag: string) =>
      enemyManager.all.find(e => e.instanceTag === tag && !e.healthComponent.isDead) ?? null;
    const statusEffects = {
      apply: (defId: string, opts: {
        sourceSkillId: string;
        durationMs: number;
        targetEntityId?: string;
        intensity?: number;
        maxStacks?: number;
        perceptionReduction?: number;
        aggroGainMultiplier?: number;
      }) => {
        const def = statusEffectSystem.getDef(defId);
        if (!def) return;
        const applyOpts = {
          sourceSkillId: opts.sourceSkillId,
          durationMs: opts.durationMs,
          intensity: opts.intensity,
          stacks: opts.maxStacks,
        };
        if (opts.targetEntityId) {
          findEnemyByTag(opts.targetEntityId)?.statusEffectTracker?.apply(defId, applyOpts);
        } else {
          // No target specified — apply to all alive enemies in proximity
          const px = player.sprite.x, py = player.sprite.y;
          const rangeSq = (2 * TILE_SIZE) * (2 * TILE_SIZE);
          for (const e of enemyManager.all) {
            if (e.healthComponent.isDead) continue;
            const dx = e.sprite.x - px, dy = e.sprite.y - py;
            if (dx * dx + dy * dy < rangeSq) {
              e.statusEffectTracker?.apply(defId, applyOpts);
            }
          }
        }
      },
      resetDuration: (defId: string, targetEntityId: string, newDurationMs: number) => {
        const enemy = findEnemyByTag(targetEntityId);
        if (!enemy?.statusEffectTracker) return;
        const instances = enemy.statusEffectTracker.getInstances(defId);
        for (const inst of instances) {
          inst.remainingMs = newDurationMs;
        }
      },
      remove: (defId: string, targetEntityId: string) => {
        if (targetEntityId) {
          findEnemyByTag(targetEntityId)?.statusEffectTracker?.remove(defId);
        } else {
          for (const e of enemyManager.all) {
            if (!e.healthComponent.isDead) e.statusEffectTracker?.remove(defId);
          }
        }
      },
    };

    // Concealment adapter — bridges to Hide/Stealth effects
    const concealment = {
      isStealthActive: () =>
        player.skillActivation.isSkillActive('stealth'),
      breakStealth: () => {
        player.skillActivation.getEffect<StealthSkillEffect>('stealth')?.breakStealth();
      },
      resetStepBudget: () => { /* no-op */ }, // STUB: deferred — step budget reset wired when Stealth internals are exposed
      forceActive: (_active: boolean) => { /* no-op */ }, // STUB: deferred — forced concealment wired in vfx-pass / combat integration
    };

    // STUB: deferred — stealth-specific combat (ambush attacks, stealth multiplier) not yet designed
    const combat = {
      startAttack: (_attackType: string) => { /* no-op */ },
      setStealthAttack: (_value: boolean) => { /* no-op */ },
    };

    // Targeting — real closures against enemyManager
    const targeting = {
      hasTargetInRange: (rangeTiles: number) => {
        const range = (rangeTiles ?? 1) * TILE_SIZE;
        return enemyManager.all.some(e => {
          if (e.healthComponent.isDead) return false;
          const dx = e.sprite.x - player.sprite.x;
          const dy = e.sprite.y - player.sprite.y;
          return Math.sqrt(dx * dx + dy * dy) <= range;
        });
      },
      shouldFizzle: () => !enemyManager.all.some(e => !e.healthComponent.isDead),
      wasTargetKilled: () => !enemyManager.all.some(e => !e.healthComponent.isDead),
      retargetNearest: (rangeTiles: number): boolean => {
        const maxDist = (rangeTiles ?? 1) * TILE_SIZE;
        return enemyManager.all.some(e => {
          if (e.healthComponent.isDead) return false;
          const dx = e.sprite.x - player.sprite.x;
          const dy = e.sprite.y - player.sprite.y;
          return Math.sqrt(dx * dx + dy * dy) <= maxDist;
        });
      },
      getTargetPosition: () => {
        const alive = enemyManager.all.find(e => !e.healthComponent.isDead);
        return alive ? { x: alive.sprite.x, y: alive.sprite.y } : { x: player.sprite.x, y: player.sprite.y };
      },
      getPlayerPosition: () => ({ x: player.sprite.x, y: player.sprite.y }),
      getCurrentPosition: () => ({ x: player.sprite.x, y: player.sprite.y }),
      getActivationPosition: () => ({ x: player.sprite.x, y: player.sprite.y }),
      getEnemiesInRadius: (radiusPx: number): string[] => {
        return enemyManager.all
          .filter(e => {
            if (e.healthComponent.isDead) return false;
            const dx = e.sprite.x - player.sprite.x;
            const dy = e.sprite.y - player.sprite.y;
            return Math.sqrt(dx * dx + dy * dy) <= radiusPx;
          })
          .map(e => e.instanceTag);
      },
      getEnemiesInRange: (): string[] => {
        return enemyManager.all
          .filter(e => !e.healthComponent.isDead)
          .map(e => e.instanceTag);
      },
      getTargets: (count: number, _worksOnAlerted: boolean): Array<{ enemyId: string; alertLevel: number }> => {
        return enemyManager.all
          .filter(e => !e.healthComponent.isDead)
          .map(e => {
            const dx = e.sprite.x - player.sprite.x;
            const dy = e.sprite.y - player.sprite.y;
            return { enemyId: e.instanceTag, alertLevel: 0, dist: Math.sqrt(dx * dx + dy * dy) };
          })
          .sort((a, b) => a.dist - b.dist)
          .slice(0, count)
          .map(({ enemyId, alertLevel }) => ({ enemyId, alertLevel }));
      },
      computeFalsePosition: (_realX: number, _realY: number) => {
        const angle = Math.random() * Math.PI * 2;
        const dist = (4 + Math.random() * 2) * TILE_SIZE;
        return { x: player.sprite.x + Math.cos(angle) * dist, y: player.sprite.y + Math.sin(angle) * dist };
      },
    };

    // AI adapter — wired methods use AIComponent; stubs deferred until backing systems exist
    const ai = {
      getEnemiesInDetectionRange: (targetX: number, targetY: number): string[] =>
        enemyManager.all
          .filter(e => {
            if (e.healthComponent.isDead) return false;
            const dx = e.sprite.x - targetX;
            const dy = e.sprite.y - targetY;
            return Math.sqrt(dx * dx + dy * dy) <= e.ai.detectionRadius;
          })
          .map(e => e.instanceTag),
      redirectToInvestigate: (_enemyId: string, _targetX: number, _targetY: number) => { /* STUB: deferred — requires AIComponent investigate entry point */ },
      getNearbyNonInvestigating: (sourceEnemyId: string, rangeTiles: number): string[] => {
        const source = enemyManager.all.find(e => e.instanceTag === sourceEnemyId);
        if (!source) return [];
        const rangePx = rangeTiles * TILE_SIZE;
        return enemyManager.all
          .filter(e => {
            if (e.healthComponent.isDead || e.instanceTag === sourceEnemyId) return false;
            if (e.ai.dutySubstate === 'investigate') return false;
            const dx = e.sprite.x - source.sprite.x;
            const dy = e.sprite.y - source.sprite.y;
            return Math.sqrt(dx * dx + dy * dy) <= rangePx;
          })
          .map(e => e.instanceTag);
      },
      plantFalsePosition: (_enemyId: string, _falseX: number, _falseY: number, _durationMs: number) => { /* STUB: deferred — requires false-position subsystem */ },
      revealPatrolRoute: (_enemyId: string) => { /* STUB: deferred — requires player-facing reveal mechanic */ },
      setSoundSourceOverride: (_enemyId: string, _pos: { x: number; y: number }) => { /* STUB: deferred — requires sound spoofing system on AIComponent */ },
      clearSoundSourceOverride: (_enemyId: string) => { /* STUB: deferred — requires sound spoofing system on AIComponent */ },
      setAggroFrozen: (_enemyId: string, _frozen: boolean) => { /* STUB: deferred — no aggroFrozen flag on AIComponent */ },
      isInvestigatingFalseSource: (_enemyId: string) => false, // STUB: deferred — no concept of "false" investigation source
      setNoReengage: (_enemyId: string, _durationMs: number) => { /* STUB: deferred — no per-enemy reentry cooldown extension on AIComponent */ },
      getNearbyEnemies: (sourceEnemyId: string, radiusPx: number): string[] => {
        const source = enemyManager.all.find(e => e.instanceTag === sourceEnemyId);
        if (!source) return [];
        return enemyManager.all
          .filter(e => {
            if (e.healthComponent.isDead || e.instanceTag === sourceEnemyId) return false;
            const dx = e.sprite.x - source.sprite.x;
            const dy = e.sprite.y - source.sprite.y;
            return Math.sqrt(dx * dx + dy * dy) <= radiusPx;
          })
          .map(e => e.instanceTag);
      },
      setCredenceVulnerable: (_enemyId: string, _durationMs: number, _damageBonus: number) => { /* STUB: deferred — no vulnerability/damage multiplier on AIComponent */ },
    };
    this._aiQueries = ai;

    // RNG adapter
    const rng = {
      roll: () => Math.random(),
      getAngle: () => Math.random() * Math.PI * 2,
    };

    // Summons adapters — different skill deps expect different spawn signatures
    // Phantom/Doppelganger: spawn(config, x, y) => string (summon ID)
    const summonsByConfig = {
      spawn: (config: unknown, x: number, y: number): string =>
        summonSystem.spawn(config as Parameters<typeof summonSystem.spawn>[0], x, y).instanceId,
    };
    // Phase/Transit: spawn(archetype, opts, x, y) => string (summon ID)
    const summonsByArchetype = {
      spawn: (archetype: string, opts: { durationMs: number; aggroWeight: number }, x: number, y: number): string =>
        summonSystem.spawn({ archetype, ...opts } as Parameters<typeof summonSystem.spawn>[0], x, y).instanceId,
    };

    // STUB: deferred — Phase intangibility requires collision-layer toggling not yet designed
    const physics = {
      setIntangible: (_active: boolean) => { /* no-op */ },
      snapToValidPosition: () => { /* no-op */ },
    };

    // Movement adapter — Transit teleport repositions the player body
    const movement = {
      teleportTo: (x: number, y: number) => {
        player.sprite.x = x;
        player.sprite.y = y;
        player.body.x = x - player.body.halfWidth;
        player.body.y = y - player.body.halfHeight;
      },
    };

    // ── Phantom path ─────────────────────────────────────────────────────

    this.register('phantom', () => new PhantomSkillEffect({
      resolver,
      targeting,
      summons: summonsByConfig,
      statusEffects,
    }));

    this.register('doppelganger', () => new DoppelgangerSkillEffect({
      resolver,
      targeting,
      summons: summonsByConfig,
      statusEffects,
    }));

    this.register('orchestrate', () => new OrchestrateSkillEffect({
      resolver,
      targeting,
      ai,
      statusEffects,
      rng,
    }));

    this.register('puppeteer', () => new PuppeteerSkillEffect({
      resolver,
      targeting,
      ai,
    }));

    // ── Veil path ────────────────────────────────────────────────────────

    this.register('veil', () => new VeilSkillEffect({
      resolver,
      targeting,
      statusEffects,
    }));

    this.register('mirage', () => new MirageSkillEffect({
      resolver,
      targeting,
      statusEffects,
    }));

    this.register('broadcast', () => new BroadcastSkillEffect({
      resolver,
      targeting,
      rng,
      ai,
      statusEffects,
    }));

    this.register('distortion', () => new DistortionSkillEffect({
      resolver,
      targeting,
      rng,
      ai,
    }));

    // ── Wraith path ──────────────────────────────────────────────────────

    // Dash adapter — bridges to DashComponent
    const dash = {
      get charges() { return player.dash.charges; },
      consumeCharge() { player.dash.charges = Math.max(0, player.dash.charges - 1); },
    };

    // Wraith movement — reposition by offset (teleport-style, no stealth break)
    const wraithMovement = {
      reposition(dx: number, dy: number) {
        player.sprite.x += dx;
        player.sprite.y += dy;
        player.body.x = player.sprite.x - player.body.halfWidth;
        player.body.y = player.sprite.y - player.body.halfHeight;
      },
    };

    // Afterimage adapter — decoy spawning
    const afterimage = {
      spawnDecoy(_x: number, _y: number, _durationMs: number) {
        // STUB: deferred — no decoy visual/entity system; wired in vfx-pass when afterimage visuals exist
      },
      getCurrentPosition: () => ({ x: player.sprite.x, y: player.sprite.y }),
    };

    // Detection adapter — find enemies along a path segment (Flicker pass-through)
    const detection = {
      getEnemiesAlongPath(x1: number, y1: number, x2: number, y2: number): string[] {
        const dx = x2 - x1;
        const dy = y2 - y1;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len === 0) return [];
        const nx = dx / len;
        const ny = dy / len;
        return enemyManager.all.filter(e => {
          if (e.healthComponent.isDead) return false;
          // Project enemy center onto the path segment
          const ex = e.sprite.x - x1;
          const ey = e.sprite.y - y1;
          const proj = ex * nx + ey * ny;
          if (proj < 0 || proj > len) return false;
          const perpDist = Math.abs(ex * ny - ey * nx);
          return perpDist <= TILE_SIZE; // Within 1 tile of the path
        }).map(e => e.instanceTag);
      },
      getPlayerPosition: () => ({ x: player.sprite.x, y: player.sprite.y }),
    };

    // Flicker AI adapter — aggro reset
    const flickerAi = {
      resetAggro(enemyId: string) {
        const enemy = enemyManager.all.find(e => e.instanceTag === enemyId);
        if (enemy) enemy.ai.resetToDuty(enemy.sprite.x, enemy.sprite.y);
      },
    };

    this.register('wraith', () => new WraithSkillEffect({
      targeting: {
        shouldFizzle: targeting.shouldFizzle,
        getFacingDirection: () => ({ x: player.facingDirection.x, y: player.facingDirection.y }),
      },
      dash,
      movement: wraithMovement,
      resolver,
      afterimage,
    }));

    this.register('flicker', () => new FlickerSkillEffect({
      targeting: {
        shouldFizzle: targeting.shouldFizzle,
        getFacingDirection: () => ({ x: player.facingDirection.x, y: player.facingDirection.y }),
      },
      dash,
      movement: wraithMovement,
      resolver,
      afterimage,
      detection,
      ai: flickerAi,
    }));

    this.register('flow_state', () => new FlowStateSkillEffect({
      player: {
        isMoving: () => {
          const vel = player.body.velocity;
          return Math.abs(vel.x) > 1 || Math.abs(vel.y) > 1;
        },
        isAttacking: () => player.combat.isAttacking,
        setMovementSpeedMultiplier: (value: number) => { player.movementSpeedMultiplier = value; },
      },
    }));

    // ── Wraith Endurance branch ─────────────────────────────────────────

    this.register('surge', () => new SurgeSkillEffect({
      targeting: { shouldFizzle: targeting.shouldFizzle },
      stamina: {
        get current() { return player.stamina.current; },
        set current(v: number) { player.stamina.current = v; },
        get max() { return player.stamina.max; },
        get drainMultiplier() { return player.stamina.drainMultiplier; },
        set drainMultiplier(v: number) { player.stamina.drainMultiplier = v; },
      },
      resolver,
    }));

    this.register('deep_reserves', () => new DeepReservesSkillEffect({
      stamina: {
        get staminaMaxBonus() { return player.stamina.staminaMaxBonus; },
        set staminaMaxBonus(v: number) { player.stamina.staminaMaxBonus = v; },
      },
    }));

    this.register('perpetual_motion', () => new PerpetualMotionSkillEffect({
      dash: {
        get overrideRefillRequiresFullStamina() { return player.dash.overrideRefillRequiresFullStamina; },
        set overrideRefillRequiresFullStamina(v: boolean | null) { player.dash.overrideRefillRequiresFullStamina = v; },
      },
    }));

    this.register('adrenaline', () => new AdrenalineSkillEffect({
      stamina: {
        get dashAttackStaminaRefundRatio() { return player.stamina.dashAttackStaminaRefundRatio; },
        set dashAttackStaminaRefundRatio(v: number) { player.stamina.dashAttackStaminaRefundRatio = v; },
      },
    }));

    // ── Wraith Evasion branch ─────────────────────────────────────────

    this.register('blink', () => new SpecterBlinkSkillEffect({
      targeting: {
        shouldFizzle: targeting.shouldFizzle,
        getFacingDirection: () => ({ x: player.facingDirection.x, y: player.facingDirection.y }),
      },
      dash: {
        get charges() { return player.dash.charges; },
        consumeCharge() { player.dash.charges = Math.max(0, player.dash.charges - 1); },
        get hasIFrames() { return player.dash.hasIFrames; },
        set hasIFrames(v: boolean) { player.dash.hasIFrames = v; },
      },
      movement: wraithMovement,
      resolver,
      afterimage,
    }));

    this.register('ghost_dash', () => new GhostDashSkillEffect({
      dash: {
        get suppressDashBreaksStealth() { return player.dash.suppressDashBreaksStealth; },
        set suppressDashBreaksStealth(v: boolean) { player.dash.suppressDashBreaksStealth = v; },
        get suppressDashGeneratesNoise() { return player.dash.suppressDashGeneratesNoise; },
        set suppressDashGeneratesNoise(v: boolean) { player.dash.suppressDashGeneratesNoise = v; },
      },
    }));

    this.register('phase_dash', () => new PhaseDashSkillEffect({
      dash: {
        get blinkIgnoresThinWalls() { return player.dash.blinkIgnoresThinWalls; },
        set blinkIgnoresThinWalls(v: boolean) { player.dash.blinkIgnoresThinWalls = v; },
      },
    }));

    this.register('extra_pip', () => new ExtraPipSkillEffect({
      dash: {
        get bonusMaxCharges() { return player.dash.bonusMaxCharges; },
        set bonusMaxCharges(v: number) { player.dash.bonusMaxCharges = v; },
      },
    }));

    // ── Apotheosis ───────────────────────────────────────────────────────

    this.register('transit', () => new TransitSkillEffect({
      targeting,
      movement,
      summons: summonsByArchetype,
    }));

    this.register('dread_sovereign', () => new DreadSovereignSkillEffect({
      targeting,
      statusEffects,
    }));

    this.register('slipstream', () => new SlipstreamSkillEffect({
      targeting: { shouldFizzle: targeting.shouldFizzle },
      player: {
        setMovementSpeedMultiplier: (value: number) => { player.movementSpeedMultiplier = value; },
        getPosition: () => ({ x: player.sprite.x, y: player.sprite.y }),
        isMoving: () => {
          const vel = player.body.velocity;
          return Math.abs(vel.x) > 1 || Math.abs(vel.y) > 1;
        },
      },
      dash: {
        get suppressDashChargeCost() { return player.dash.suppressDashChargeCost; },
        set suppressDashChargeCost(v: boolean) { player.dash.suppressDashChargeCost = v; },
      },
      detection: {
        getEnemiesInRadius(radiusPx: number): string[] {
          const px = player.sprite.x;
          const py = player.sprite.y;
          const r2 = radiusPx * radiusPx;
          return enemyManager.all.filter(e => {
            if (e.healthComponent.isDead) return false;
            const dx = e.sprite.x - px;
            const dy = e.sprite.y - py;
            return dx * dx + dy * dy <= r2;
          }).map(e => e.instanceTag);
        },
        getPlayerPosition: () => ({ x: player.sprite.x, y: player.sprite.y }),
      },
      ai: flickerAi,
      afterimage,
    }));

    // --- Terminal passives ---

    this.register('steady_pace', () => new SteadyPaceSkillEffect({
      stamina: {
        get walkingRechargeRate() { return player.stamina.walkingRechargeRate; },
        set walkingRechargeRate(v: number) { player.stamina.walkingRechargeRate = v; },
      },
    }));

    this.register('quick_recovery', () => new QuickRecoverySkillEffect({
      stamina: {
        get rechargeMultiplier() { return player.stamina.rechargeMultiplier; },
        set rechargeMultiplier(v: number) { player.stamina.rechargeMultiplier = v; },
      },
    }));

    this.register('tireless', () => new TirelessSkillEffect({
      stamina: {
        get drainMultiplier() { return player.stamina.drainMultiplier; },
        set drainMultiplier(v: number) { player.stamina.drainMultiplier = v; },
      },
    }));

    this.register('sanctuary', () => new SanctuarySkillEffect({
      get concealedHealthRegenRate() { return player.concealedHealthRegenRate; },
      set concealedHealthRegenRate(v: number) { player.concealedHealthRegenRate = v; },
    }));
  }

  /** Register all Fire tree active/evolution/apotheosis skills. */
  private _registerFireSkills(): void {
    const { player, enemyManager, upgradeResolver: resolver, stationaryTracker, statusEffectSystem, zoneSystem, playerProjectilePool, knockbackSystem } = this.deps;

    // Shared adapters — same patterns as Grit/Blades/Specter

    const breakHide = () => {
      player.skillActivation.getEffect<HideSkillEffect>('hide')?.breakHide();
    };

    // Melee range check — true if any living enemy within 1 tile
    const isInMeleeRange = () => {
      const range = TILE_SIZE;
      return enemyManager.all.some(e => {
        if (e.healthComponent.isDead) return false;
        const dx = e.sprite.x - player.sprite.x;
        const dy = e.sprite.y - player.sprite.y;
        return Math.sqrt(dx * dx + dy * dy) <= range;
      });
    };

    // Cone targeting — find enemy IDs inside a cone from the player
    const getConeTargets = (lengthTiles: number, angleRangeDeg: number, facingAngle: number): string[] => {
      const maxDist = lengthTiles * TILE_SIZE;
      const halfArc = (angleRangeDeg / 2) * (Math.PI / 180);
      return enemyManager.all
        .filter(e => {
          if (e.healthComponent.isDead) return false;
          const dx = e.sprite.x - player.sprite.x;
          const dy = e.sprite.y - player.sprite.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > maxDist || dist === 0) return false;
          const angle = Math.atan2(dy, dx);
          let diff = angle - facingAngle;
          // Normalize to [-PI, PI]
          while (diff > Math.PI) diff -= 2 * Math.PI;
          while (diff < -Math.PI) diff += 2 * Math.PI;
          return Math.abs(diff) <= halfArc;
        })
        .map(e => e.instanceTag);
    };
    const getPlayerFacing = () => Math.atan2(player.facingDirection.y, player.facingDirection.x);
    const getTargetPosition = () => ({ x: player.sprite.x, y: player.sprite.y });

    // Entity query — find enemy IDs within radius of a world position
    const getEnemiesInRadius = (x: number, y: number, radiusTiles: number): string[] => {
      const maxDist = radiusTiles * TILE_SIZE;
      return enemyManager.all
        .filter(e => {
          if (e.healthComponent.isDead) return false;
          const dx = e.sprite.x - x;
          const dy = e.sprite.y - y;
          return Math.sqrt(dx * dx + dy * dy) <= maxDist;
        })
        .map(e => e.instanceTag);
    };
    const dealDamage = (entityId: string, damage: number) => {
      const target = enemyManager.all.find(e => e.instanceTag === entityId);
      if (target && !target.healthComponent.isDead) {
        dealDamageToEnemy(target, damage);
      }
    };

    // Entity lookup helpers (same pattern as Blades/Specter/Poison)
    const findEnemyByTag = (tag: string) =>
      enemyManager.all.find(e => e.instanceTag === tag && !e.healthComponent.isDead);
    const nearestAliveEnemy = (maxRangePx: number) => {
      let best: import('../entities/Enemy').Enemy | null = null;
      let bestDistSq = maxRangePx * maxRangePx;
      const px = player.sprite.x, py = player.sprite.y;
      for (const e of enemyManager.all) {
        if (e.healthComponent.isDead) continue;
        const dx = e.sprite.x - px, dy = e.sprite.y - py;
        const dSq = dx * dx + dy * dy;
        if (dSq < bestDistSq) { bestDistSq = dSq; best = e; }
      }
      return best;
    };

    // Status effect adapters (wired to real StatusEffectTracker)
    const applyEnemyStatus = (
      entityId: string, statusId: string,
      opts: { durationMs: number; intensity?: number; sourceSkillId?: string },
    ) => {
      const def = statusEffectSystem.getDef(statusId);
      if (!def) return;
      const target = findEnemyByTag(entityId);
      target?.statusEffectTracker?.apply(statusId, {
        sourceSkillId: opts.sourceSkillId ?? 'unknown',
        durationMs: opts.durationMs,
        intensity: opts.intensity,
      });
    };

    const applyPlayerStatus = (
      statusId: string,
      opts: { durationMs: number; sourceSkillId: string },
    ) => {
      const def = statusEffectSystem.getDef(statusId);
      if (!def) return;
      player.statusEffectTracker?.apply(statusId, {
        sourceSkillId: opts.sourceSkillId,
        durationMs: opts.durationMs,
      });
    };

    // Player adapter
    const playerAdapter = {
      get maxHp() { return player.health.max; },
      heal: (amount: number) => { player.health.heal(amount); },
    };

    // Zone adapter — bridges to ZoneSystem
    const zones = {
      create: (config: unknown): string =>
        zoneSystem.create(config as Parameters<typeof zoneSystem.create>[0]),
      createAshField: (config: unknown): string =>
        zoneSystem.create(config as Parameters<typeof zoneSystem.create>[0]),
      remove: (zoneId: string) => { zoneSystem.remove(zoneId); },
      moveZone: (zoneId: string, dx: number, dy: number, tiles: number) => {
        zoneSystem.moveZone(zoneId, dx, dy, tiles);
      },
      isInsideType: (x: number, y: number, zoneType: string) =>
        zoneSystem.isInsideType(x, y, zoneType),
    };

    // Projectile adapter for Shape bolts — delegates to PlayerProjectilePool.
    // ShapeSkillEffect passes a partial config (no speed); fill in defaults.
    const projectiles = {
      fire: (config: unknown) => {
        const c = config as Partial<import('../systems/PlayerProjectilePool').PlayerProjectileConfig>
          & { type: import('../systems/PlayerProjectilePool').ProjectileType; damage: number; sourceSkillId: string };
        const full: import('../systems/PlayerProjectilePool').PlayerProjectileConfig = {
          speed: 300,
          rangeTiles: 6,
          ...c,
        };
        const px = player.sprite.x, py = player.sprite.y;
        const fx = player.facingDirection.x, fy = player.facingDirection.y;
        playerProjectilePool.fire(full, px, py, fx, fy);
      },
    };

    // Resolver adapter for skills with 2-arg getTreePassiveEffect (Ember, Warmth, Bonfire)
    // All tree passives accessed through this path use 'max' aggregation
    const resolverWith2ArgTree = {
      getAdditiveEffect: (skillId: string, key: string) => resolver.getAdditiveEffect(skillId, key),
      hasEffect: (skillId: string, key: string) => resolver.hasEffect(skillId, key),
      getTreePassiveEffect: (treeId: string, key: string) =>
        resolver.getTreePassiveEffect(treeId, key, 'max'),
    };

    // Bonfire resolver adds getMultiplicativeEffect
    const bonfireResolver = {
      ...resolverWith2ArgTree,
      getMultiplicativeEffect: (skillId: string, key: string) =>
        resolver.getMultiplicativeEffect(skillId, key),
    };

    // Stationary duration from StationaryTracker
    const getStationaryDurationMs = () => stationaryTracker.getStationaryDurationMs();

    // ── Trunk ──────────────────────────────────────────────────────────────

    // Declarative dispatch helper: builds ResolverContext and calls engine
    const fireDispatch = (skillId: string): ((trigger: string, targetId?: string) => void) => {
      return (trigger: string, targetId?: string) => {
        const targetEnemy = targetId ? findEnemyByTag(targetId) : null;
        const ctx: ResolverContext = {
          getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
          getMaxEffect: (s, k) => resolver.getMaxEffect(s, k) ?? 0,
          getMultiplicativeEffect: (s, k) => resolver.getMultiplicativeEffect(s, k),
          getTreePassiveEffect: (tree, key, mode) => resolver.getTreePassiveEffect(tree, key, mode),
          hasEffect: (s, k) => resolver.hasEffect(s, k),
          isImmune: (effectId) =>
            (targetEnemy ?? nearestAliveEnemy(2 * TILE_SIZE))?.statusEffectTracker?.isImmune?.(effectId) ?? false,
        };
        this.dispatchDeclarativeEffects(skillId, trigger as EffectTrigger, ctx, targetEnemy);
      };
    };

    this.register('lighter', () => new LighterSkillEffect({
      dispatchEffects: fireDispatch('lighter'),
      isInMeleeRange,
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
    }));

    this.register('conduit', () => new ConduitSkillEffect({
      dispatchEffects: fireDispatch('conduit'),
      getConeTargets,
      getPlayerFacing,
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
      breakHide,
    }));

    // ── Vas Flam path ──────────────────────────────────────────────────────

    this.register('vas_flam', () => new VasFlamSkillEffect({
      resolver,
      dispatchEffects: fireDispatch('vas_flam'),
      getConeTargets,
      getPlayerFacing,
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
      breakHide,
      dealDamage,
    }));

    this.register('conflagration', () => new ConflagrationSkillEffect({
      resolver,
      getTargetBurningInstances: (targetId) => {
        const enemy = findEnemyByTag(targetId);
        if (!enemy?.statusEffectTracker) return [];
        return enemy.statusEffectTracker.getInstances('burning').map(inst => ({
          stacks: inst.stacks,
          intensity: inst.intensity,
          remainingMs: inst.remainingMs,
          resolvedTickIntervalMs: inst.resolvedTickIntervalMs,
          sourceSkillId: inst.sourceSkillId,
        }));
      },
      clearTargetBurning: (targetId) => {
        findEnemyByTag(targetId)?.statusEffectTracker?.remove('burning');
      },
      applyBurning: (targetId, config) => {
        const def = statusEffectSystem.getDef('burning');
        if (!def) return;
        findEnemyByTag(targetId)?.statusEffectTracker?.apply('burning', {
          sourceSkillId: config.sourceSkillId,
          durationMs: config.durationMs,
          intensity: config.intensity,
          stacks: config.stacks,
          thresholdMs: config.thresholdMs,
        });
      },
      getConeTargets,
      getPlayerFacing,
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
      breakHide,
      dealDamage,
    }));

    this.register('cultivate', () => new CultivateSkillEffect({
      resolver,
      getPlayerPosition: () => ({ x: player.sprite.x, y: player.sprite.y }),
      getPlayerEntityId: () => 'player',
      getShoveDirection: () => null, // STUB: deferred — no input-state query for held direction
      breakHide,
      zones,
    }));

    this.register('shape', () => new ShapeSkillEffect({
      resolver,
      applyContactBurn: (entityId, intensity, durationMs, sourceSkillId) => {
        const def = statusEffectSystem.getDef('burning');
        if (!def) return;
        findEnemyByTag(entityId)?.statusEffectTracker?.apply('burning', {
          sourceSkillId,
          durationMs,
          intensity,
        });
      },
      getActivationMode: () => 'bolt', // STUB: deferred — no tap/hold input detection; defaults to bolt mode
      getPlayerAimAngle: () => Math.atan2(player.facingDirection.y, player.facingDirection.x),
      getPlayerPosition: () => ({ x: player.sprite.x, y: player.sprite.y }),
      getPlayerEntityId: () => 'player',
      getNearestFireZoneId: () => {
        const px = player.sprite.x, py = player.sprite.y;
        let bestId: string | null = null;
        let bestDistSq = Infinity;
        for (const z of zoneSystem.getAll()) {
          const t = z.config.type;
          if (t !== 'fire_zone' && t !== 'fire_wall' && t !== 'bonfire_zone') continue;
          const dx = z.config.position.x - px, dy = z.config.position.y - py;
          const dSq = dx * dx + dy * dy;
          if (dSq < bestDistSq) { bestDistSq = dSq; bestId = z.config.id; }
        }
        return bestId;
      },
      getShoveDirection: () => null, // STUB: deferred — no input-state query for held direction
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
      breakHide,
      zones,
      projectiles,
    }));

    // ── Backdraft path ─────────────────────────────────────────────────────

    this.register('backdraft', () => new BackdraftSkillEffect({
      resolver,
      findNearestDetonatableZone: (x, y, types, maxTiles) => {
        const maxDistSq = (maxTiles * TILE_SIZE) * (maxTiles * TILE_SIZE);
        let best: import('../skills/player/fire/BackdraftSkillEffect').BackdraftZoneInfo | null = null;
        let bestDistSq = Infinity;
        for (const z of zoneSystem.getAll()) {
          if (!types.includes(z.config.type)) continue;
          const dx = z.config.position.x - x, dy = z.config.position.y - y;
          const dSq = dx * dx + dy * dy;
          if (dSq > maxDistSq || dSq >= bestDistSq) continue;
          bestDistSq = dSq;
          const elapsed = z.config.durationMs > 0 ? z.config.durationMs - z.remainingMs : 0;
          best = {
            id: z.config.id,
            type: z.config.type,
            position: z.config.position,
            elapsedMs: elapsed,
            durationMs: z.config.durationMs,
            radiusTiles: z.currentRadiusTiles,
          };
        }
        return best;
      },
      zones,
      getEnemiesInRadius,
      dealDamage,
      applyKnockback: (fromX, fromY, entityId, tiles) => {
        const enemy = findEnemyByTag(entityId);
        if (!enemy) return;
        const dx = enemy.sprite.x - fromX;
        const dy = enemy.sprite.y - fromY;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        const distPx = tiles * TILE_SIZE;
        const config = ConfigLoader.combat.knockback;
        knockbackSystem.applyRaw(enemy, { x: dx / len, y: dy / len }, distPx * config.velocityMultiplier, config.durationMs);
      },
      applyEnemyStatus,
      isImmune: (_entityId: string, statusId: string) =>
        nearestAliveEnemy(2 * TILE_SIZE)?.statusEffectTracker?.isImmune(statusId) ?? false,
      getPlayerPosition: () => ({ x: player.sprite.x, y: player.sprite.y }),
      breakHide,
    }));

    this.register('flashpoint', () => new FlashpointSkillEffect({
      resolver,
      findNearestDetonatableZone: (x, y, types, maxTiles) => {
        const maxDistSq = (maxTiles * TILE_SIZE) * (maxTiles * TILE_SIZE);
        let best: import('../skills/player/fire/FlashpointSkillEffect').FlashpointZoneInfo | null = null;
        let bestDistSq = Infinity;
        for (const z of zoneSystem.getAll()) {
          if (!types.includes(z.config.type)) continue;
          const dx = z.config.position.x - x, dy = z.config.position.y - y;
          const dSq = dx * dx + dy * dy;
          if (dSq > maxDistSq || dSq >= bestDistSq) continue;
          bestDistSq = dSq;
          const elapsed = z.config.durationMs > 0 ? z.config.durationMs - z.remainingMs : 0;
          best = {
            id: z.config.id,
            type: z.config.type,
            position: z.config.position,
            elapsedMs: elapsed,
            durationMs: z.config.durationMs,
            radiusTiles: z.currentRadiusTiles,
            dotIntensity: z.config.dotIntensity,
            tickIntervalMs: z.config.tickIntervalMs,
          };
        }
        return best;
      },
      zones,
      getEnemiesInRadius,
      dealDamage,
      applyKnockback: (fromX, fromY, entityId, tiles) => {
        const enemy = findEnemyByTag(entityId);
        if (!enemy) return;
        const dx = enemy.sprite.x - fromX;
        const dy = enemy.sprite.y - fromY;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        const distPx = tiles * TILE_SIZE;
        const config = ConfigLoader.combat.knockback;
        knockbackSystem.applyRaw(enemy, { x: dx / len, y: dy / len }, distPx * config.velocityMultiplier, config.durationMs);
      },
      applyEnemyStatus,
      isImmune: (entityId: string, statusId: string) =>
        findEnemyByTag(entityId)?.statusEffectTracker?.isImmune(statusId) ?? false,
      hasEnemyStatus: (entityId: string, statusId: string) =>
        findEnemyByTag(entityId)?.statusEffectTracker?.has(statusId) ?? false,
      refreshEnemyBurning: (entityId, opts) => {
        const enemy = findEnemyByTag(entityId);
        const inst = enemy?.statusEffectTracker?.get('burning');
        if (inst) {
          inst.remainingMs = opts.durationMs;
        } else {
          const def = statusEffectSystem.getDef('burning');
          if (!def) return;
          enemy?.statusEffectTracker?.apply('burning', {
            sourceSkillId: opts.sourceSkillId,
            durationMs: opts.durationMs,
          });
        }
      },
      getPlayerPosition: () => ({ x: player.sprite.x, y: player.sprite.y }),
      breakHide,
    }));

    this.register('choke', () => new ChokeSkillEffect({
      resolver,
      shouldFizzle: () => !player.skillProgression.rollSkillSuccess('choke'),
      getTargetPosition,
      breakHide,
      zones,
      applyEnemyStatus,
    }));

    this.register('cauterize', () => new CauterizeSkillEffect({
      applyPlayerStatus,
      player: playerAdapter,
      resolver: {
        hasEffect: (skillId, key) => resolver.hasEffect(skillId, key),
        getMaxEffect: (skillId, key) => resolver.getMaxEffect(skillId, key),
      },
      getPlayerDotStatuses: () => {
        const all = player.statusEffectTracker?.getAll() ?? [];
        const dotIds = new Set<string>();
        for (const inst of all) {
          if (inst.def.category === 'dot') dotIds.add(inst.def.id);
        }
        return Array.from(dotIds);
      },
      removePlayerStatus: (statusId: string) => { player.statusEffectTracker?.remove(statusId); },
      applyExplosion: (position, config) => {
        const targets = getEnemiesInRadius(position.x, position.y, config.radiusTiles);
        for (const entityId of targets) {
          dealDamage(entityId, config.damage);
          applyEnemyStatus(entityId, config.statusToApply, {
            durationMs: config.statusDurationMs,
            sourceSkillId: config.sourceSkillId,
          });
        }
      },
    }));

    // ── Ember path ─────────────────────────────────────────────────────────

    this.register('ember', () => new EmberSkillEffect({
      getPlayerPosition: () => ({ x: player.sprite.x, y: player.sprite.y }),
      getPlayerEntityId: () => 'player',
      getEnemiesInRadius,
      dealDamage,
      player: playerAdapter,
      breakHide,
      zones,
      resolver: resolverWith2ArgTree,
      getStationaryDurationMs,
      applyEnemyStatus,
    }));

    this.register('warmth', () => new WarmthSkillEffect({
      getPlayerPosition: () => ({ x: player.sprite.x, y: player.sprite.y }),
      getPlayerEntityId: () => 'player',
      getEnemiesInRadius,
      dealDamage,
      player: playerAdapter,
      breakHide,
      zones,
      resolver: resolverWith2ArgTree,
      applyEnemyStatus,
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
      getStationaryDurationMs,
    }));

    this.register('temper', () => new TemperSkillEffect({
      isInMeleeRange,
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
      hasTargetStatus: (statusId) => {
        const target = nearestAliveEnemy(TILE_SIZE);
        return target?.statusEffectTracker?.has(statusId) ?? false;
      },
      makeBurningPermanent: () => {
        const target = nearestAliveEnemy(TILE_SIZE);
        const inst = target?.statusEffectTracker?.get('burning');
        if (inst) inst.remainingMs = -1;
      },
      applyTargetStatus: (statusId, opts) => {
        const def = statusEffectSystem.getDef(statusId);
        if (!def) return;
        const target = nearestAliveEnemy(TILE_SIZE);
        target?.statusEffectTracker?.apply(statusId, {
          sourceSkillId: opts.sourceSkillId,
          durationMs: opts.durationMs,
        });
      },
      resolver: {
        hasEffect: (skillId, key) => resolver.hasEffect(skillId, key),
        getAdditiveEffect: (skillId, key) => resolver.getAdditiveEffect(skillId, key),
      },
      findNearestEnemyWithStatus: (position: { x: number; y: number }, statusId: string, radiusTiles: number) => {
        const maxDist = radiusTiles * TILE_SIZE;
        let best: { id: string; dist: number } | null = null;
        for (const e of enemyManager.all) {
          if (e.healthComponent.isDead) continue;
          if (!e.statusEffectTracker?.has(statusId)) continue;
          const dx = e.sprite.x - position.x, dy = e.sprite.y - position.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist <= maxDist && (!best || dist < best.dist)) {
            best = { id: e.instanceTag, dist };
          }
        }
        return best?.id ?? null;
      },
      makeBurningPermanentOnEnemy: (enemyId) => {
        const inst = findEnemyByTag(enemyId)?.statusEffectTracker?.get('burning');
        if (inst) inst.remainingMs = -1;
      },
      applyStatusToEnemy: (enemyId: string, statusId: string, opts: { durationMs?: number; intensity?: number; sourceSkillId?: string }) => {
        const def = statusEffectSystem.getDef(statusId);
        if (!def) return;
        findEnemyByTag(enemyId)?.statusEffectTracker?.apply(statusId, {
          sourceSkillId: opts.sourceSkillId ?? 'unknown',
          durationMs: opts.durationMs,
          intensity: opts.intensity,
        });
      },
    }));

    this.register('bonfire', () => new BonfireSkillEffect({
      getPlayerPosition: () => ({ x: player.sprite.x, y: player.sprite.y }),
      getPlayerEntityId: () => 'player',
      getEnemiesInRadius,
      dealDamage,
      player: playerAdapter,
      breakHide,
      zones,
      resolver: bonfireResolver,
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
      getStationaryDurationMs,
    }));

    // ── Apotheosis ─────────────────────────────────────────────────────────

    this.register('immolate', () => new ImmolateSkillEffect({
      applyPlayerStatus,
      getPlayerPosition: () => ({ x: player.sprite.x, y: player.sprite.y }),
      breakHide,
      zones,
    }));

    this.register('ashfall', () => new AshfallSkillEffect({
      resolver,
      getTargetPosition,
      getEnemiesInRadius,
      dealDamage,
      applyEnemyStatus,
      zones,
      breakHide,
    }));

    this.register('hearthfire', () => new HearthfireSkillEffect({
      getPlayerPosition: () => ({ x: player.sprite.x, y: player.sprite.y }),
      getEnemiesInRadius,
      applyEnemyStatus,
      applyPlayerStatus,
      player: playerAdapter,
      getFireZonesInLineOfSight: () => {
        // Returns all qualifying fire zones. LoS filtering requires groundLayer
        // access (Bresenham raycast) which is not available in the factory deps.
        // TODO: Add LoS filtering when groundLayer is wired into SkillEffectDeps.
        const result: Array<{ id: string; x: number; y: number }> = [];
        for (const z of zoneSystem.getAll()) {
          const t = z.config.type;
          if (t === 'fire_zone' || t === 'fire_wall' || t === 'bonfire_zone') {
            result.push({ id: z.config.id, x: z.config.position.x, y: z.config.position.y });
          }
        }
        return result;
      },
      breakHide,
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
    }));
  }

  /** Register all Poison tree active/evolution/passive/apotheosis skills. */
  private _registerPoisonSkills(): void {
    const {
      player,
      enemyManager,
      upgradeResolver: resolver,
      zoneSystem,
      deathTriggerSystem,
      chainTargetingSystem,
      cooldownHookSystem,
      playerProjectilePool,
    } = this.deps;

    // ── Shared adapters ──────────────────────────────────────────────────

    // Status-effects adapter — routes to per-entity StatusEffectTracker instances.
    // Poison calling convention: apply(defId, { sourceSkillId, durationMs?, intensity?, ... })
    // Poison also has entity-targeted helpers: applyPoison, hasPoisonStatus, etc.
    const { statusEffectSystem } = this.deps;
    const findEnemyByTag = (tag: string) =>
      enemyManager.all.find(e => e.instanceTag === tag && !e.healthComponent.isDead) ?? null;
    const nearestAliveEnemy = (maxRangePx: number) => {
      let best: import('../entities/Enemy').Enemy | null = null;
      let bestDistSq = maxRangePx * maxRangePx;
      const px = player.sprite.x, py = player.sprite.y;
      for (const e of enemyManager.all) {
        if (e.healthComponent.isDead) continue;
        const dx = e.sprite.x - px, dy = e.sprite.y - py;
        const dSq = dx * dx + dy * dy;
        if (dSq < bestDistSq) { bestDistSq = dSq; best = e; }
      }
      return best;
    };
    const statusEffects = {
      apply: (defId: string, opts: {
        sourceSkillId: string;
        durationMs?: number;
        intensity?: number;
        stackingRuleOverride?: string;
        targetEntityId?: string;
      }) => {
        const def = statusEffectSystem.getDef(defId);
        if (!def) return;
        const applyOpts: import('../systems/StatusEffectTracker').ApplyOptions = {
          sourceSkillId: opts.sourceSkillId,
          durationMs: opts.durationMs,
          intensity: opts.intensity,
          stackingRuleOverride: opts.stackingRuleOverride as import('../systems/StatusEffectTracker').ApplyOptions['stackingRuleOverride'],
        };
        if (opts.targetEntityId) {
          findEnemyByTag(opts.targetEntityId)?.statusEffectTracker?.apply(defId, applyOpts);
        } else {
          nearestAliveEnemy(2 * TILE_SIZE)?.statusEffectTracker?.apply(defId, applyOpts);
        }
      },
      remove: (defId: string) => {
        for (const e of enemyManager.all) {
          if (!e.healthComponent.isDead) e.statusEffectTracker?.remove(defId);
        }
      },
      hasPoisonStatus: (entityId: string): boolean => {
        return findEnemyByTag(entityId)?.statusEffectTracker?.has('poison') ?? false;
      },
      getPoisonInstances: (entityId: string): ReadonlyArray<{
        intensity: number; remainingMs: number; stacks: number; tickIntervalMs: number;
      }> => {
        const enemy = findEnemyByTag(entityId);
        if (!enemy?.statusEffectTracker) return [];
        return enemy.statusEffectTracker.getInstances('poison').map(inst => ({
          intensity: inst.intensity,
          remainingMs: inst.remainingMs,
          stacks: inst.stacks,
          tickIntervalMs: inst.resolvedTickIntervalMs ?? inst.def.defaultTickIntervalMs ?? 1000,
        }));
      },
      hasDebuff: (entityId: string, defId: string): boolean => {
        return findEnemyByTag(entityId)?.statusEffectTracker?.has(defId) ?? false;
      },
      removePoisonStatus: (entityId: string) => {
        findEnemyByTag(entityId)?.statusEffectTracker?.remove('poison');
      },
      applyPoison: (
        entityId: string, durationMs: number, intensity: number, sourceSkillId: string,
      ) => {
        findEnemyByTag(entityId)?.statusEffectTracker?.apply('poison', {
          sourceSkillId, durationMs, intensity,
        });
      },
      extendPoison: (entityId: string, extensionMs: number) => {
        const enemy = findEnemyByTag(entityId);
        if (!enemy?.statusEffectTracker) return;
        const instances = enemy.statusEffectTracker.getInstances('poison');
        for (const inst of instances) {
          if (inst.remainingMs > 0) inst.remainingMs += extensionMs;
        }
      },
      applyToEntity: (
        entityId: string, defId: string,
        opts: { sourceSkillId: string; intensity: number; durationMs: number; stackingRuleOverride?: unknown },
      ) => {
        const def = statusEffectSystem.getDef(defId);
        if (!def) return;
        findEnemyByTag(entityId)?.statusEffectTracker?.apply(defId, {
          sourceSkillId: opts.sourceSkillId,
          intensity: opts.intensity,
          durationMs: opts.durationMs,
          stackingRuleOverride: opts.stackingRuleOverride as import('../systems/StatusEffectTracker').ApplyOptions['stackingRuleOverride'],
        });
      },
    };

    // Targeting — real enemy-based proximity checks
    const targeting = {
      getTargetPosition: () => {
        let best: { x: number; y: number; dist: number } | null = null;
        for (const e of enemyManager.all) {
          if (e.healthComponent.isDead) continue;
          const dx = e.sprite.x - player.sprite.x;
          const dy = e.sprite.y - player.sprite.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (!best || dist < best.dist) best = { x: e.sprite.x, y: e.sprite.y, dist };
        }
        return best ? { x: best.x, y: best.y } : { x: player.sprite.x, y: player.sprite.y };
      },
      getPlayerPosition: () => ({ x: player.sprite.x, y: player.sprite.y }),
      getEnemiesInRange: (
        center: { x: number; y: number }, rangeTiles: number,
      ): ReadonlyArray<{ entityId: string; position: { x: number; y: number } }> => {
        const maxDist = rangeTiles * TILE_SIZE;
        return enemyManager.all
          .filter(e => {
            if (e.healthComponent.isDead) return false;
            const dx = e.sprite.x - center.x;
            const dy = e.sprite.y - center.y;
            return Math.sqrt(dx * dx + dy * dy) <= maxDist;
          })
          .map(e => ({ entityId: e.instanceTag, position: { x: e.sprite.x, y: e.sprite.y } }));
      },
      getCurrentTargetId: () => {
        let best: { id: string; dist: number } | null = null;
        for (const e of enemyManager.all) {
          if (e.healthComponent.isDead) continue;
          const dx = e.sprite.x - player.sprite.x;
          const dy = e.sprite.y - player.sprite.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (!best || dist < best.dist) best = { id: e.instanceTag, dist };
        }
        return best?.id ?? '';
      },
      isTargetPoisoned: () => {
        const targetId = targeting.getCurrentTargetId();
        if (!targetId) return false;
        return findEnemyByTag(targetId)?.statusEffectTracker?.has('poison') ?? false;
      },
    };

    // Chain targeting adapter — bridges to ChainTargetingSystem
    const chainTargeting = {
      findChainTargets: (config: unknown, candidates: Iterable<unknown>) =>
        chainTargetingSystem.findChainTargets(
          config as Parameters<typeof chainTargetingSystem.findChainTargets>[0],
          candidates as Iterable<Parameters<typeof chainTargetingSystem.findChainTargets>[1] extends Iterable<infer T> ? T : never>,
        ),
    };

    // RNG adapter
    const rng = { roll: () => Math.random() };

    // Damage adapter — burst damage routed to HealthComponent
    const damage = {
      applyBurst: (entityId: string, amount: number, _sourceSkillId: string) => {
        const target = findEnemyByTag(entityId);
        if (target) {
          dealDamageToEnemy(target, amount);
        }
      },
    };

    // Death trigger adapter
    const deathTriggers = {
      register: (config: Parameters<typeof deathTriggerSystem.register>[0]) => {
        deathTriggerSystem.register(config);
      },
      unregister: (triggerId: string) => { deathTriggerSystem.unregister(triggerId); },
    };

    // Zone adapter — bridges to ZoneSystem
    const zones = {
      create: (config: unknown): string =>
        zoneSystem.create(config as Parameters<typeof zoneSystem.create>[0]),
      remove: (zoneId: string) => { zoneSystem.remove(zoneId); },
      getZone: (zoneId: string) => zoneSystem.getZone(zoneId),
      getEntityDwellTimeMs: (zoneId: string, entityId: string) =>
        zoneSystem.getEntityDwellTimeMs(zoneId, entityId),
      getAll: () => zoneSystem.getAll(),
    };

    // STUB: deferred — no corpse tracking system exists; corpse poison mechanics need entity death + loot system
    const corpses = {
      getPoisonedCorpsesInRange: (
        _center: { x: number; y: number }, _rangeTiles: number,
      ): ReadonlyArray<{ corpseId: string; position: { x: number; y: number } }> => [],
      getPoisonInstances: (_corpseId: string): ReadonlyArray<{
        intensity: number; remainingMs: number; stacks: number; tickIntervalMs: number;
      }> => [],
      removePoisonFromCorpse: (_corpseId: string) => { /* no-op */ },
    };

    // Cooldown hooks adapter
    const cooldownHooks = {
      notify: (trigger: Parameters<typeof cooldownHookSystem.notify>[0], baseCooldowns?: Map<string, number>) => {
        cooldownHookSystem.notify(trigger, baseCooldowns);
      },
    };

    // Player adapter
    const playerAdapter = {
      get maxHp() { return player.health.max; },
      get currentHp() { return player.health.hp; },
      getMaxHp: () => player.health.max,
      getCurrentHpRatio: () => player.health.hp / player.health.max,
      heal: (amount: number) => { player.health.heal(amount); },
    };

    // Slow effect — apply 'slow' debuff to enemy via StatusEffectTracker
    const applySlowEffect = (
      entityId: string,
      opts: { sourceSkillId: string; durationMs: number; intensity: number },
    ) => {
      findEnemyByTag(entityId)?.statusEffectTracker?.apply('slow', {
        sourceSkillId: opts.sourceSkillId,
        durationMs: opts.durationMs,
        intensity: opts.intensity,
      });
    };

    // Entity status — apply arbitrary status to enemy via StatusEffectTracker
    const applyEntityStatus = (
      entityId: string, defId: string,
      opts: { sourceSkillId: string; durationMs: number; intensity: number },
    ) => {
      findEnemyByTag(entityId)?.statusEffectTracker?.apply(defId, {
        sourceSkillId: opts.sourceSkillId,
        durationMs: opts.durationMs,
        intensity: opts.intensity,
      });
    };

    const getEntityEffectStacks = (entityId: string, defId: string): number =>
      findEnemyByTag(entityId)?.statusEffectTracker?.getStacks(defId) ?? 0;

    // ── Trunk ──────────────────────────────────────────────────────────────

    this.register('taint', () => new TaintSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getTreePassiveEffect: (t, k, a) => resolver.getTreePassiveEffect(t, k, a),
      },
      statusEffects,
    }));

    this.register('virulent_touch', () => new VirulentTouchSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getTreePassiveEffect: (t, k, a) => resolver.getTreePassiveEffect(t, k, a),
      },
      statusEffects,
    }));

    // ── Corrode path ───────────────────────────────────────────────────────

    this.register('corrode', () => new CorrodeSkillEffect({
      zoneSystem: zones,
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getTreePassiveEffect: (t, k, a) => resolver.getTreePassiveEffect(t, k, a),
      },
      player: {
        get x() { return player.sprite.x; },
        get y() { return player.sprite.y; },
        get facingAngle() { return Math.atan2(player.facingDirection.y, player.facingDirection.x); },
      },
    }));

    this.register('corrode_evolved', () => new MiasmaSkillEffect({
      zoneSystem: zones,
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getTreePassiveEffect: (t, k, a) => resolver.getTreePassiveEffect(t, k, a),
      },
      player: {
        get x() { return player.sprite.x; },
        get y() { return player.sprite.y; },
        get facingAngle() { return Math.atan2(player.facingDirection.y, player.facingDirection.x); },
      },
      applySlowEffect,
    }));

    this.register('haze', () => new HazeSkillEffect({
      zoneSystem: zones,
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k) ?? 0,
        getTreePassiveEffect: (t, k, a) => resolver.getTreePassiveEffect(t, k, a),
      },
      targeting,
      applyEntityStatus,
      getEntityEffectStacks,
      applySlowEffect,
    }));

    this.register('acid_rain', () => new AcidRainSkillEffect({
      zoneSystem: zones,
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getTreePassiveEffect: (t, k, a) => resolver.getTreePassiveEffect(t, k, a),
      },
      targeting,
      projectilePool: {
        fire: (config, fromX, fromY, dirX, dirY) =>
          playerProjectilePool.fire(config, fromX, fromY, dirX, dirY),
      },
      playerPosition: {
        getPosition: () => ({ x: player.sprite.x, y: player.sprite.y }),
      },
      applySlowEffect,
    }));

    this.register('blight', () => new BlightSkillEffect({
      zoneSystem: zones,
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getTreePassiveEffect: (t, k, a) => resolver.getTreePassiveEffect(t, k, a),
      },
      targeting,
      applyEntityStatus,
      getEntityEffectStacks,
      applySlowEffect,
    }));

    // ── Plague path ────────────────────────────────────────────────────────

    this.register('plague_vector', () => new PlagueVectorSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
        getTreePassiveEffect: (t, k, a) => resolver.getTreePassiveEffect(t, k, a),
      },
      statusEffects,
      chainTargeting,
      target: targeting,
      rng,
    }));

    this.register('plague_evolved', () => new ContagionSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
        getTreePassiveEffect: (t, k, a) => resolver.getTreePassiveEffect(t, k, a),
      },
      statusEffects,
      chainTargeting,
      target: targeting,
      rng,
    }));

    this.register('plague_path_passive', () => new CarrierPassiveSkillEffect({
      resolver: {
        hasEffect: (s, k) => resolver.hasEffect(s, k),
      },
      deathTriggers,
    }));

    this.register('chain', () => new ChainSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getTreePassiveEffect: (t, k, a) => resolver.getTreePassiveEffect(t, k, a),
      },
      statusEffects,
      chainTargeting,
      target: targeting,
      cooldownHooks,
      rng,
    }));

    this.register('chain_passive', () => new VectorsPassiveSkillEffect({
      resolver: {
        getTreePassiveEffect: (t, k, a) => resolver.getTreePassiveEffect(t, k, a),
      },
    }));

    this.register('charnel_ground', () => new CharnelGroundSkillEffect({
      zoneSystem: zones,
      deathTriggers,
      targeting,
    }));

    this.register('entropy', () => new EntropySkillEffect({
      targeting,
      statusEffects,
      damage,
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
    }));

    // ── Expel path ─────────────────────────────────────────────────────────

    this.register('expel', () => new ExpelSkillEffect({
      zoneSystem: zones,
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        hasEffect: (s, k) => resolver.hasEffect(s, k),
      },
      player: playerAdapter,
    }));

    this.register('expel_retaliate', () => new VenombloodSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getTreePassiveEffect: (t, k, a) => resolver.getTreePassiveEffect(t, k, a),
      },
      statusEffects,
    }));

    this.register('expel_evolved', () => new ExpelMkIISkillEffect({
      zoneSystem: zones,
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        hasEffect: (s, k) => resolver.hasEffect(s, k),
      },
      player: playerAdapter,
      statusEffects,
    }));

    this.register('expel_path_passive', () => new AbsorptionPassiveSkillEffect({
      resolver: {
        getTreePassiveEffect: (t, k, a) => resolver.getTreePassiveEffect(t, k, a),
      },
      player: playerAdapter,
      isExpelActive: () =>
        (player.skillActivation.getEffect<ExpelSkillEffect>('expel')
          ?? player.skillActivation.getEffect<ExpelMkIISkillEffect>('expel_evolved'))
          ?.isActive() ?? false,
    }));

    this.register('ecdysis', () => new EcdysisSkillEffect({
      targeting,
      statusEffects,
      damage,
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        hasEffect: (s, k) => resolver.hasEffect(s, k),
        getTreePassiveEffect: (t, k, a) => resolver.getTreePassiveEffect(t, k, a as 'sum' | 'product' | 'max' | 'min'),
      },
      expel: {
        getToxinLoad: () => {
          const effect = player.skillActivation.getEffect<ExpelSkillEffect>('expel')
            ?? player.skillActivation.getEffect<ExpelMkIISkillEffect>('expel_evolved');
          return effect?.toxinLoad ?? 0;
        },
        consumeToxinLoad: () => {
          // Ecdysis consumes all toxin load — Expel doesn't expose a direct setter,
          // so we deactivate the aura (which is the gameplay intent: shed and restart)
          const effect = player.skillActivation.getEffect<ExpelSkillEffect>('expel')
            ?? player.skillActivation.getEffect<ExpelMkIISkillEffect>('expel_evolved');
          effect?.deactivate?.();
        },
        isActive: () =>
          (player.skillActivation.getEffect<ExpelSkillEffect>('expel')
            ?? player.skillActivation.getEffect<ExpelMkIISkillEffect>('expel_evolved'))
            ?.isActive() ?? false,
        forceReactivate: () => {
          (player.skillActivation.getEffect<ExpelSkillEffect>('expel')
            ?? player.skillActivation.getEffect<ExpelMkIISkillEffect>('expel_evolved'))
            ?.forceReactivate();
        },
      },
      zoneSystem: zones,
    }));

    this.register('ecdysis_passive', () => new ResiduePassiveSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
    }));

    this.register('corpse_bloom', () => new CorpseBloomSkillEffect({
      targeting,
      corpses,
      statusEffects,
      damage,
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
    }));

    this.register('corpse_bloom_passive', () => new VirulentRemainsPassiveSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
      deathTriggers,
    }));

    this.register('synthesis', () => new SynthesisSkillEffect({
      targeting,
      statusEffects,
      zones: {
        getAll: () => zoneSystem.getAll(),
        remove: (zoneId: string) => { zoneSystem.remove(zoneId); },
      },
      player: playerAdapter,
    }));
  }

  /** Register all Lightning tree active/evolution/passive-with-runtime skills. */
  private _registerLightningSkills(): void {
    const {
      player,
      enemyManager,
      upgradeResolver: resolver,
      chargeSystem,
      chainTargetingSystem,
      zoneSystem,
      playerProjectilePool,
      knockbackSystem,
    } = this.deps;

    // ── Shared adapters ──────────────────────────────────────────────────

    const breakHide = () => {
      player.skillActivation.getEffect<HideSkillEffect>('hide')?.breakHide();
    };

    // Status-effects adapter (wired to real StatusEffectTracker)
    const { statusEffectSystem } = this.deps;
    const nearestAliveEnemy = (maxRangePx: number) => {
      let best: import('../entities/Enemy').Enemy | null = null;
      let bestDistSq = maxRangePx * maxRangePx;
      const px = player.sprite.x, py = player.sprite.y;
      for (const e of enemyManager.all) {
        if (e.healthComponent.isDead) continue;
        const dx = e.sprite.x - px, dy = e.sprite.y - py;
        const dSq = dx * dx + dy * dy;
        if (dSq < bestDistSq) { bestDistSq = dSq; best = e; }
      }
      return best;
    };
    const findEnemyByTag = (tag: string) =>
      enemyManager.all.find(e => e.instanceTag === tag && !e.healthComponent.isDead);
    const statusEffects = {
      apply: (defId: string, opts: {
        sourceSkillId: string;
        durationMs: number;
        intensity?: number;
        stacks?: number;
      }) => {
        const def = statusEffectSystem.getDef(defId);
        if (!def) return;
        if (def.category === 'buff') {
          player.statusEffectTracker?.apply(defId, opts);
        } else {
          const target = nearestAliveEnemy(2 * TILE_SIZE);
          target?.statusEffectTracker?.apply(defId, opts);
        }
      },
      isImmune: (defId: string): boolean =>
        nearestAliveEnemy(2 * TILE_SIZE)?.statusEffectTracker?.isImmune(defId) ?? false,
      has: (defId: string): boolean =>
        nearestAliveEnemy(2 * TILE_SIZE)?.statusEffectTracker?.has(defId) ?? false,
      remove: (defId: string) => {
        const def = statusEffectSystem.getDef(defId);
        if (!def) return;
        if (def.category === 'buff') {
          player.statusEffectTracker?.remove(defId);
        } else {
          for (const e of enemyManager.all) {
            if (!e.healthComponent.isDead) e.statusEffectTracker?.remove(defId);
          }
        }
      },
    };

    // Targeting — find nearest living enemy within range (tile-distance)
    const getTarget = (rangeTiles: number): string | null => {
      const maxDist = (rangeTiles ?? 1) * TILE_SIZE;
      let best: { id: string; dist: number } | null = null;
      for (const e of enemyManager.all) {
        if (e.healthComponent.isDead) continue;
        const dx = e.sprite.x - player.sprite.x;
        const dy = e.sprite.y - player.sprite.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist <= maxDist && (!best || dist < best.dist)) {
          best = { id: e.instanceTag, dist };
        }
      }
      return best?.id ?? null;
    };
    const getTargetPosition = (targetId?: string) => {
      if (targetId) {
        const e = enemyManager.all.find(en => en.instanceTag === targetId);
        if (e && !e.healthComponent.isDead) return { x: e.sprite.x, y: e.sprite.y };
      }
      return { x: player.sprite.x, y: player.sprite.y };
    };
    const getPlayerPosition = () => ({ x: player.sprite.x, y: player.sprite.y });
    const getEnemiesInRadius = (x: number, y: number, radiusTiles: number): string[] => {
      const maxDist = radiusTiles * TILE_SIZE;
      return enemyManager.all
        .filter(e => {
          if (e.healthComponent.isDead) return false;
          const dx = e.sprite.x - x;
          const dy = e.sprite.y - y;
          return Math.sqrt(dx * dx + dy * dy) <= maxDist;
        })
        .map(e => e.instanceTag);
    };

    // Damage — deal damage to a specific enemy by instance tag
    const dealDamage = (targetId: string, damage: number) => {
      const target = enemyManager.all.find(e => e.instanceTag === targetId);
      if (target && !target.healthComponent.isDead) {
        dealDamageToEnemy(target, damage);
      }
    };

    // Charge adapter — bridges to ChargeSystem
    const charge = {
      consume: (chargeId: string, amount?: number) =>
        amount != null ? chargeSystem.consume(chargeId, amount) : chargeSystem.consume(chargeId),
      get: (chargeId: string) => chargeSystem.get(chargeId),
      getCurrent: (chargeId: string) => chargeSystem.get(chargeId)?.current ?? 0,
      getMax: (chargeId: string) => chargeSystem.get(chargeId)?.maxCharge ?? 0,
      addCharge: (chargeId: string, amount: number) => { chargeSystem.addCharge(chargeId, amount); },
    };

    // Chain targeting adapter — bridges to ChainTargetingSystem
    const findChainTargets = (config: unknown, candidates?: Iterable<unknown>) =>
      chainTargetingSystem.findChainTargets(
        config as Parameters<typeof chainTargetingSystem.findChainTargets>[0],
        candidates as Iterable<Parameters<typeof chainTargetingSystem.findChainTargets>[1] extends Iterable<infer T> ? T : never>,
      );

    // Zone adapter — bridges to ZoneSystem
    const zones = {
      create: (config: unknown): string =>
        zoneSystem.create(config as Parameters<typeof zoneSystem.create>[0]),
    };

    // AoE adapters — find enemies in radius, apply damage, return hit counts.
    // Damage: ConfigLoader.getDamageConfig(sourceSkillId).baseDamage × damageMultiplier.
    const aoeHit = (config: unknown) => {
      const { rangeTiles, damageMultiplier, sourceSkillId } =
        config as { rangeTiles: number; radiusTiles: number; damageMultiplier: number; sourceSkillId: string };
      const baseDamage = ConfigLoader.getDamageConfig(sourceSkillId).baseDamage;
      const damage = baseDamage * damageMultiplier;
      const px = player.sprite.x, py = player.sprite.y;
      const tags = getEnemiesInRadius(px, py, rangeTiles);
      for (const tag of tags) dealDamage(tag, damage);
      return { enemiesHit: tags.length, hitX: px, hitY: py };
    };
    const aoeBurst = (config: unknown) => {
      const { radiusTiles, damageMultiplier, sourceSkillId } =
        config as { radiusTiles: number; damageMultiplier: number; sourceSkillId: string };
      const baseDamage = ConfigLoader.getDamageConfig(sourceSkillId).baseDamage;
      const damage = baseDamage * damageMultiplier;
      const px = player.sprite.x, py = player.sprite.y;
      const tags = getEnemiesInRadius(px, py, radiusTiles);
      for (const tag of tags) dealDamage(tag, damage);
      return { enemiesHit: tags.length };
    };

    // Projectile adapters — fire via PlayerProjectilePool.
    const projectileFire = (config: unknown): number => {
      const { damage, sourceSkillId, targetEntityId } =
        config as { damage: number; sourceSkillId: string; targetEntityId: string };
      const targetPos = getTargetPosition(targetEntityId);
      const px = player.sprite.x, py = player.sprite.y;
      const dx = targetPos.x - px, dy = targetPos.y - py;
      const id = playerProjectilePool.fire(
        { type: 'bolt', damage, speed: 400, rangeTiles: 6, sourceSkillId },
        px, py, dx, dy,
      );
      return id >= 0 ? 1 : 0;
    };
    const projectileFireAtNearest = (config: unknown) => {
      const { maxTargets, damageMultiplier, sourceSkillId } =
        config as { maxTargets: number; damageMultiplier: number; sourceSkillId: string };
      const baseDamage = ConfigLoader.getDamageConfig(sourceSkillId).baseDamage;
      const damage = baseDamage * damageMultiplier;
      const px = player.sprite.x, py = player.sprite.y;
      const hits: { readonly hitX: number; readonly hitY: number }[] = [];
      // Find nearest living enemies up to maxTargets
      const candidates: { tag: string; x: number; y: number; distSq: number }[] = [];
      for (const e of enemyManager.all) {
        if (e.healthComponent.isDead) continue;
        const dx = e.sprite.x - px, dy = e.sprite.y - py;
        candidates.push({ tag: e.instanceTag, x: e.sprite.x, y: e.sprite.y, distSq: dx * dx + dy * dy });
      }
      candidates.sort((a, b) => a.distSq - b.distSq);
      for (const c of candidates.slice(0, maxTargets)) {
        const dx = c.x - px, dy = c.y - py;
        const id = playerProjectilePool.fire(
          { type: 'bolt', damage, speed: 400, rangeTiles: 8, sourceSkillId },
          px, py, dx, dy,
        );
        if (id >= 0) hits.push({ hitX: c.x, hitY: c.y });
      }
      return { hits: hits as ReadonlyArray<{ readonly hitX: number; readonly hitY: number }> };
    };

    // Player status adapters (wired to real player StatusEffectTracker)
    const applyPlayerStatus = (statusId: string, config: { durationMs: number; intensity: number }) => {
      const def = statusEffectSystem.getDef(statusId);
      if (!def) return;
      player.statusEffectTracker?.apply(statusId, {
        sourceSkillId: 'lightning',
        durationMs: config.durationMs,
        intensity: config.intensity,
      });
    };
    const hasPlayerStatus = (statusId: string): boolean =>
      player.statusEffectTracker?.has(statusId) ?? false;
    const removePlayerStatus = (statusId: string) => {
      player.statusEffectTracker?.remove(statusId);
    };
    const consumeStatus = (targetId: string, statusId: string) => {
      findEnemyByTag(targetId)?.statusEffectTracker?.remove(statusId);
    };
    const isInsideZoneType = (type: string): boolean =>
      zoneSystem.isInsideType(player.sprite.x, player.sprite.y, type);

    // ── Trunk (spark is foundation) ─────────────────────────────────────

    this.register('spark', () => new SparkSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k) => resolver.getMultiplicativeEffect(s, k),
      },
      statusEffects,
      getTarget,
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
      breakHide,
      dealDamage,
    }));

    this.register('jolt', () => new JoltSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k) => resolver.getMultiplicativeEffect(s, k),
      },
      statusEffects,
      getTarget,
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
      breakHide,
      dealDamage,
    }));

    this.register('static_buildup', () => new StaticBuildupSkillEffect({
      chargeSystem,
      resolver: {
        getTreePassiveEffect: (t, k, a) =>
          resolver.getTreePassiveEffect(t, k, a as 'sum' | 'product' | 'max' | 'min'),
      },
    }));

    // ── Arc path ────────────────────────────────────────────────────────

    this.register('arc', () => new ArcSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k) => resolver.getMultiplicativeEffect(s, k),
        hasEffect: (s, k) => resolver.hasEffect(s, k),
      },
      consumeCharge: (chargeId) => charge.consume(chargeId),
      findChainTargets: (config) => findChainTargets(config) as unknown as ArcChainResult[],
      getTarget,
      getTargetPosition: (targetId) => getTargetPosition(targetId),
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
      breakHide,
      dealDamage,
    }));

    this.register('chain_lightning', () => new ChainLightningSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k) => resolver.getMultiplicativeEffect(s, k),
        hasEffect: (s, k) => resolver.hasEffect(s, k),
      },
      consumeCharge: (chargeId) => charge.consume(chargeId),
      findChainTargets: (config) => findChainTargets(config) as unknown as ChainLightningChainResult[],
      getTarget,
      getTargetPosition: (targetId) => getTargetPosition(targetId),
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
      breakHide,
      dealDamage,
      getTreePassiveEffect: (t, k, a) => resolver.getTreePassiveEffect(t, k, a),
      applyPlayerStatus,
      consumeStatus,
      isInsideZoneType,
    }));

    this.register('arc_path_passive', () => new GatheringStormSkillEffect({
      hasPlayerStatus,
      removePlayerStatus,
      resolver: {
        getTreePassiveEffect: (t, k, a) =>
          resolver.getTreePassiveEffect(t, k, a as 'sum' | 'product' | 'max' | 'min'),
      },
    }));

    // ── Bolt sub-path ───────────────────────────────────────────────────

    this.register('bolt', () => new BoltSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k) => resolver.getMultiplicativeEffect(s, k),
      },
      statusEffects,
      consumeCharge: (chargeId) => charge.consume(chargeId),
      getTarget,
      getTargetPosition: (targetId) => getTargetPosition(targetId),
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
      rollChance: (chance) => Math.random() < chance,
      breakHide,
      dealDamage,
      spawnZone: (config) => { zones.create(config); },
    }));

    // ── Stormcaller sub-path ────────────────────────────────────────────

    this.register('stormcaller', () => new StormcallerSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
      statusEffects,
      consumeCharge: (chargeId) => charge.consume(chargeId),
      getEnemiesInRadius,
      getTargetPosition: () => getTargetPosition(),
      getPlayerPosition,
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
      breakHide,
      dealDamage,
    }));

    // ── Arc apotheosis ──────────────────────────────────────────────────

    this.register('tempest', () => new TempestSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k) => resolver.getMultiplicativeEffect(s, k),
        hasEffect: (s, k) => resolver.hasEffect(s, k),
      },
      chain: {
        findChainTargets: (config) => findChainTargets(config) as unknown as TempestChainResult,
      },
      getPlayerPosition: () => ({ x: player.sprite.x, y: player.sprite.y }),
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
      breakHide,
      charge: {
        consume: (chargeId) => charge.consume(chargeId),
      },
    }));

    // ── Pulse path ──────────────────────────────────────────────────────

    this.register('pulse', () => new PulseSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k) => resolver.getMultiplicativeEffect(s, k),
      },
      aoe: { hit: (config) => aoeHit(config) },
      statusEffects,
      charge: { consume: (chargeId) => charge.consume(chargeId) },
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
      breakHide,
      zones: { create: (config) => { zones.create(config); } },
    }));

    this.register('seizure', () => new SeizureSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k) => resolver.getMultiplicativeEffect(s, k),
      },
      aoe: { hit: (config) => aoeHit(config) },
      statusEffects,
      charge: { consume: (chargeId) => charge.consume(chargeId) },
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
      breakHide,
      zones: { create: (config) => { zones.create(config); } },
    }));

    // ── Seize sub-path ──────────────────────────────────────────────────

    this.register('seize', () => new SeizeSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k) => resolver.getMultiplicativeEffect(s, k),
      },
      throw: {
        execute: (config) => {
          const e = enemyManager.all.find(en => !en.healthComponent.isDead);
          if (!e) return { targetAcquired: false, impact: 'none' as const };
          const fd = player.facingDirection;
          const len = Math.sqrt(fd.x * fd.x + fd.y * fd.y) || 1;
          const dir = { x: fd.x / len, y: fd.y / len };
          const cfg = config as { maxDistanceTiles: number };
          const distPx = (cfg.maxDistanceTiles ?? 3) * TILE_SIZE;
          const kbConfig = ConfigLoader.combat.knockback;
          knockbackSystem.applyRaw(e, dir, distPx * kbConfig.velocityMultiplier, kbConfig.durationMs);
          return { targetAcquired: true, impact: 'none' as const };
        },
      },
      statusEffects,
      struckStatusEffects: statusEffects,
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
      breakHide,
    }));

    // ── Dead Zone sub-path ──────────────────────────────────────────────

    this.register('dead_zone', () => new DeadZoneSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
      zones: { create: (config) => { zones.create(config); } },
      charge: { consume: (chargeId) => charge.consume(chargeId) },
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
      breakHide,
    }));

    // ── Pulse apotheosis ────────────────────────────────────────────────

    this.register('hijack', () => new HijackSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k) => resolver.getMultiplicativeEffect(s, k),
      },
      target: {
        findDisruptedEnemy: () => {
          const e = enemyManager.all.find(en => !en.healthComponent.isDead);
          return e ? { acquired: true, entityId: e.instanceTag } : { acquired: false };
        },
      },
      statusEffects,
      control: {
        // STUB: deferred — requires dedicated hijack control system (AI suspension,
        // faction inversion, player input delegation). forceSimState is test-only
        // and applyStagger only freezes without control transfer. Build-then-wire.
        beginHijack: () => {},
        endHijack: () => {},
        isTargetAlive: () => enemyManager.all.some(e => !e.healthComponent.isDead),
      },
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
      breakHide,
    }));

    // ── Discharge path ──────────────────────────────────────────────────

    this.register('discharge', () => new DischargeSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k) => resolver.getMultiplicativeEffect(s, k),
      },
      aoe: { burst: (config) => aoeBurst(config) },
      charge: { consume: (chargeId) => charge.consume(chargeId) },
      statusEffects,
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
      breakHide,
      zones: { create: (config) => { zones.create(config); } },
    }));

    this.register('overload', () => new OverloadSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k) => resolver.getMultiplicativeEffect(s, k),
      },
      aoe: { burst: (config) => aoeBurst(config) },
      charge: {
        consume: (chargeId, amount) => charge.consume(chargeId, amount),
        get: (chargeId) => charge.get(chargeId),
      },
      statusEffects,
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
      breakHide,
      zones: { create: (config) => { zones.create(config); } },
    }));

    // ── Lightning Reflexes sub-path ─────────────────────────────────────

    this.register('lightning_reflexes', () => new LightningReflexesSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k) => resolver.getMultiplicativeEffect(s, k),
      },
      projectiles: { fire: (config) => projectileFire(config) },
      charge: {
        getCurrent: (chargeId) => charge.getCurrent(chargeId),
        getMax: (chargeId) => charge.getMax(chargeId),
        addCharge: (chargeId, amount) => { charge.addCharge(chargeId, amount); },
      },
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
      breakHide,
    }));

    // ── Arc Storm sub-path ──────────────────────────────────────────────

    this.register('arc_storm', () => new ArcStormSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k) => resolver.getMultiplicativeEffect(s, k),
      },
      projectiles: { fireAtNearest: (config) => projectileFireAtNearest(config) },
      charge: { consume: (chargeId) => charge.consume(chargeId) },
      statusEffects,
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
      breakHide,
      zones: { create: (config) => { zones.create(config); } },
      melee: {
        getTargetsInRange: (radiusTiles) => {
          const maxDist = radiusTiles * TILE_SIZE;
          return enemyManager.all
            .filter(e => {
              if (e.healthComponent.isDead) return false;
              const dx = e.sprite.x - player.sprite.x;
              const dy = e.sprite.y - player.sprite.y;
              return Math.sqrt(dx * dx + dy * dy) <= maxDist;
            })
            .map(e => ({ id: e.instanceTag }));
        },
      },
      enemyCombat: {
        dealDamage: (enemyId, amount, _sourceSkillId) => {
          const target = enemyManager.all.find(e => e.instanceTag === enemyId);
          if (target && !target.healthComponent.isDead) {
            dealDamageToEnemy(target, amount);
          }
        },
      },
    }));

    // ── Discharge apotheosis ────────────────────────────────────────────

    this.register('becomes_the_storm', () => new BecomesTheStormSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k) => resolver.getMultiplicativeEffect(s, k),
      },
      aoe: { burst: (config) => aoeBurst(config) },
      charge: {
        consume: (chargeId, amount) => charge.consume(chargeId, amount),
        get: (chargeId) => charge.get(chargeId),
      },
      statusEffects,
      rollSkillSuccess: (id) => player.skillProgression.rollSkillSuccess(id),
      breakHide,
      zones: { create: (config) => { zones.create(config); } },
      melee: {
        getTargetsInRange: (radiusTiles) => {
          const maxDist = radiusTiles * TILE_SIZE;
          return enemyManager.all
            .filter(e => {
              if (e.healthComponent.isDead) return false;
              const dx = e.sprite.x - player.sprite.x;
              const dy = e.sprite.y - player.sprite.y;
              return Math.sqrt(dx * dx + dy * dy) <= maxDist;
            })
            .map(e => ({ id: e.instanceTag }));
        },
      },
      enemyCombat: {
        dealDamage: (enemyId, amount, _sourceSkillId) => {
          const target = enemyManager.all.find(e => e.instanceTag === enemyId);
          if (target && !target.healthComponent.isDead) {
            dealDamageToEnemy(target, amount);
          }
        },
      },
    }));
  }

  /** Register all Earth tree active/evolution/passive/apotheosis skills. */
  private _registerEarthSkills(): void {
    const {
      player,
      enemyManager,
      upgradeResolver: resolver,
      zoneSystem,
      stationaryTracker,
      terrainConstructionSystem: terrain,
      cooldownHookSystem,
      knockbackSystem,
      groundLayer,
    } = this.deps;

    // ── Shared adapters ──────────────────────────────────────────────────

    const breakHide = () => {
      player.skillActivation.getEffect<HideSkillEffect>('hide')?.breakHide();
    };

    // Stationary adapter — bridges to StationaryTracker
    const stationary = {
      isStationary: (thresholdMs: number) => stationaryTracker.isStationary(thresholdMs),
    };

    // Status-effects adapter (wired to real StatusEffectTracker)
    const { statusEffectSystem } = this.deps;
    const nearestAliveEnemy = (maxRangePx: number) => {
      let best: import('../entities/Enemy').Enemy | null = null;
      let bestDistSq = maxRangePx * maxRangePx;
      const px = player.sprite.x, py = player.sprite.y;
      for (const e of enemyManager.all) {
        if (e.healthComponent.isDead) continue;
        const dx = e.sprite.x - px, dy = e.sprite.y - py;
        const dSq = dx * dx + dy * dy;
        if (dSq < bestDistSq) { bestDistSq = dSq; best = e; }
      }
      return best;
    };
    const findEnemyByTag = (tag: string) =>
      enemyManager.all.find(e => e.instanceTag === tag && !e.healthComponent.isDead);
    const statusEffects = {
      apply: (effectId: string, ...args: unknown[]) => {
        const def = statusEffectSystem.getDef(effectId);
        if (!def) return;
        // Earth skills may pass positional args: (durationMs, sourceSkillId) or config object
        let sourceSkillId = 'unknown';
        let durationMs: number | undefined;
        let intensity: number | undefined;
        for (const arg of args) {
          if (typeof arg === 'string') sourceSkillId = arg;
          else if (typeof arg === 'number') {
            if (durationMs === undefined) durationMs = arg;
            else intensity = arg;
          } else if (typeof arg === 'object' && arg !== null) {
            const obj = arg as Record<string, unknown>;
            if (typeof obj.sourceSkillId === 'string') sourceSkillId = obj.sourceSkillId;
            if (typeof obj.durationMs === 'number') durationMs = obj.durationMs;
            if (typeof obj.intensity === 'number') intensity = obj.intensity;
          }
        }
        const target = nearestAliveEnemy(2 * TILE_SIZE);
        target?.statusEffectTracker?.apply(effectId, { sourceSkillId, durationMs, intensity });
      },
    };

    // Targeting — wired to enemyManager spatial queries
    const getEnemiesInRadiusByPx = (radiusPx: number): string[] => {
      const px = player.sprite.x, py = player.sprite.y;
      const maxDistSq = radiusPx * radiusPx;
      return enemyManager.all
        .filter(e => {
          if (e.healthComponent.isDead) return false;
          const dx = e.sprite.x - px, dy = e.sprite.y - py;
          return dx * dx + dy * dy <= maxDistSq;
        })
        .map(e => e.instanceTag);
    };
    const getEnemiesInRadiusByPos = (
      center: { x: number; y: number }, radiusTiles: number,
    ): string[] => {
      const maxDist = radiusTiles * TILE_SIZE;
      const maxDistSq = maxDist * maxDist;
      return enemyManager.all
        .filter(e => {
          if (e.healthComponent.isDead) return false;
          const dx = e.sprite.x - center.x, dy = e.sprite.y - center.y;
          return dx * dx + dy * dy <= maxDistSq;
        })
        .map(e => e.instanceTag);
    };
    const getSingleTarget = (): string | null =>
      nearestAliveEnemy(Infinity)?.instanceTag ?? null;
    const getImpactPosition = (): { x: number; y: number } => {
      const target = nearestAliveEnemy(Infinity);
      return target
        ? { x: target.sprite.x, y: target.sprite.y }
        : { x: player.sprite.x, y: player.sprite.y };
    };
    const getConeTargets = (arcDegrees: number, maxTargets: number): string[] => {
      const halfArc = (arcDegrees / 2) * (Math.PI / 180);
      const fx = player.facingDirection.x, fy = player.facingDirection.y;
      const facingAngle = Math.atan2(fy, fx);
      const hits: Array<{ tag: string; distSq: number }> = [];
      const px = player.sprite.x, py = player.sprite.y;
      for (const e of enemyManager.all) {
        if (e.healthComponent.isDead) continue;
        const dx = e.sprite.x - px, dy = e.sprite.y - py;
        const distSq = dx * dx + dy * dy;
        if (distSq === 0) continue;
        const angle = Math.atan2(dy, dx);
        let diff = angle - facingAngle;
        while (diff > Math.PI) diff -= 2 * Math.PI;
        while (diff < -Math.PI) diff += 2 * Math.PI;
        if (Math.abs(diff) <= halfArc) {
          hits.push({ tag: e.instanceTag, distSq });
        }
      }
      hits.sort((a, b) => a.distSq - b.distSq);
      return hits.slice(0, maxTargets).map(h => h.tag);
    };
    const getLineTargets = (
      facingAngleDeg: number, lengthTiles: number,
    ): string[] => {
      const maxDist = lengthTiles * TILE_SIZE;
      const angleRad = facingAngleDeg * (Math.PI / 180);
      const dirX = Math.cos(angleRad), dirY = Math.sin(angleRad);
      const halfWidth = TILE_SIZE / 2;
      const px = player.sprite.x, py = player.sprite.y;
      return enemyManager.all
        .filter(e => {
          if (e.healthComponent.isDead) return false;
          const dx = e.sprite.x - px, dy = e.sprite.y - py;
          // Project onto line direction
          const proj = dx * dirX + dy * dirY;
          if (proj < 0 || proj > maxDist) return false;
          // Perpendicular distance
          const perp = Math.abs(dx * (-dirY) + dy * dirX);
          return perp <= halfWidth;
        })
        .map(e => e.instanceTag);
    };
    const getTargetPosition = (
      maxRangeTiles: number,
    ): { x: number; y: number } => {
      const maxRange = maxRangeTiles * TILE_SIZE;
      const target = nearestAliveEnemy(maxRange);
      return target
        ? { x: target.sprite.x, y: target.sprite.y }
        : { x: player.sprite.x, y: player.sprite.y };
    };
    const getTargetPoint = (
      rangeTiles: number,
    ): { position: { x: number; y: number }; footprintTiles: Array<{ x: number; y: number }> } => {
      const maxRange = rangeTiles * TILE_SIZE;
      const target = nearestAliveEnemy(maxRange);
      return target
        ? { position: { x: target.sprite.x, y: target.sprite.y }, footprintTiles: [] }
        : { position: { x: player.sprite.x, y: player.sprite.y }, footprintTiles: [] };
    };
    const getTargetedEnemy = (
      rangeTiles: number,
    ): { enemyId: string; position: { x: number; y: number }; footprintTiles: Array<{ x: number; y: number }> } | null => {
      const range = rangeTiles * TILE_SIZE;
      const target = nearestAliveEnemy(range);
      if (!target) return null;
      return {
        enemyId: target.instanceTag,
        position: { x: target.sprite.x, y: target.sprite.y },
        footprintTiles: [],
      };
    };
    const getEnemiesAtPosition = (
      position: { x: number; y: number },
    ): string[] => {
      const threshold = TILE_SIZE;
      const thresholdSq = threshold * threshold;
      return enemyManager.all
        .filter(e => {
          if (e.healthComponent.isDead) return false;
          const dx = e.sprite.x - position.x, dy = e.sprite.y - position.y;
          return dx * dx + dy * dy <= thresholdSq;
        })
        .map(e => e.instanceTag);
    };
    const getEnemyPosition = (
      enemyId: string,
    ): { x: number; y: number } => {
      const enemy = findEnemyByTag(enemyId);
      return enemy
        ? { x: enemy.sprite.x, y: enemy.sprite.y }
        : { x: 0, y: 0 };
    };
    const getPlayerPosition = (): { x: number; y: number } => ({
      x: player.sprite.x, y: player.sprite.y,
    });

    // Combat stubs — wired in vfx-pass / combat integration
    const applyDamage = (
      targetId: string, damage: number, _opts: { sourceSkillId: string; ignoreKnockbackResistance?: boolean },
    ) => {
      const target = findEnemyByTag(targetId);
      if (target) {
        dealDamageToEnemy(target, damage);
      }
    };
    const applyKnockback = (
      targetId: string, normalizedDir: { x: number; y: number }, tiles: number,
    ) => {
      const enemy = enemyManager.all.find(e => e.instanceTag === targetId && !e.healthComponent.isDead);
      if (!enemy) return;
      const distPx = tiles * TILE_SIZE;
      const config = ConfigLoader.combat.knockback;
      knockbackSystem.applyRaw(enemy, normalizedDir, distPx * config.velocityMultiplier, config.durationMs);
    };

    // Zone adapter — bridges to ZoneSystem
    const zones = {
      create: (config: unknown): string =>
        zoneSystem.create(config as Parameters<typeof zoneSystem.create>[0]),
      remove: (zoneId: string) => { zoneSystem.remove(zoneId); },
      isInsideOwnTerrain: (): boolean => {
        const px = player.sprite.x, py = player.sprite.y;
        return zoneSystem.isInsideType(px, py, 'crater')
          || zoneSystem.isInsideType(px, py, 'rough_terrain');
      },
    };

    // Terrain adapter — bridges to TerrainConstructionSystem
    const terrainAdapter = {
      place: (config: unknown): string =>
        terrain.place(config as Parameters<typeof terrain.place>[0]),
      remove: (terrainId: string) => { terrain.remove(terrainId); },
      getAll: () => terrain.getAll(),
      getActiveCountInRadius: (
        type: string, x: number, y: number, radiusTiles: number,
      ): number => terrain.getActiveCountInRadius(type, x, y, radiusTiles, TILE_SIZE),
      getFormationsInRadius: (
        x: number, y: number, radiusTiles: number,
      ): Array<{ id: string; position: { x: number; y: number } }> =>
        terrain.getFormationsInRadius(x, y, radiusTiles, TILE_SIZE),
      getActiveCount: () => terrain.getActiveCount(),
    };

    // STUB: deferred — A* updates wired in vfx-pass / integration
    const pathfinding = {
      markImpassable: (positions: Array<{ x: number; y: number }>) => {
        for (const pos of positions) {
          const tileX = Math.floor(pos.x / TILE_SIZE);
          const tileY = Math.floor(pos.y / TILE_SIZE);
          const tile = groundLayer.getTileAt(tileX, tileY);
          if (tile) {
            if (!tile.properties) tile.properties = {};
            tile.properties.bufferBlocked = true;
          }
        }
      },
    };

    // STUB: deferred — no cursor/facing placement system; returns player pos with empty footprint
    const placement = {
      getWallPlacement: (): { position: { x: number; y: number }; footprintTiles: Array<{ x: number; y: number }> } => ({
        position: { x: player.sprite.x, y: player.sprite.y },
        footprintTiles: [],
      }),
      getFormationPlacement: (_rangeTiles: number): { position: { x: number; y: number }; footprintTiles: Array<{ x: number; y: number }> } => ({
        position: { x: player.sprite.x, y: player.sprite.y },
        footprintTiles: [],
      }),
    };

    // Shared Fortify player state — mutable object read by StandingWave + Fortify
    const fortifyPlayerState: FortifyPlayerState = {
      fortifyShieldHp: 0,
      fortifyStaggerAbsorbsRemaining: 0,
    };

    // Player-state adapter for Petrify — delegates to PlayerStateComponent
    const petrifyPlayerState = {
      setPetrified: (active: boolean) => { player.playerState.setPetrified('petrify', active); },
      setDamageReduction: (sourceSkillId: string, value: number) => { player.playerState.setDamageReduction(sourceSkillId, value); },
      setKnockbackImmune: (sourceSkillId: string, immune: boolean) => { player.playerState.setKnockbackImmune(sourceSkillId, immune); },
      setStaggerImmune: (sourceSkillId: string, immune: boolean) => { player.playerState.setStaggerImmune(sourceSkillId, immune); },
      setMovementLocked: (sourceSkillId: string, locked: boolean) => { player.playerState.setMovementLocked(sourceSkillId, locked); },
    };

    // Cooldown hooks adapter — bridges to CooldownHookSystem
    const cooldownHooks = {
      register: (hook: Parameters<typeof cooldownHookSystem.register>[0]) => {
        cooldownHookSystem.register(hook);
      },
      unregister: (hookId: string) => { cooldownHookSystem.unregister(hookId); },
    };

    // ── Trunk ──────────────────────────────────────────────────────────────

    this.register('stomp', () => new StompSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
      statusEffects,
      targeting: { getEnemiesInRadius: getEnemiesInRadiusByPx },
      player: { breakHide },
    }));

    this.register('stomp_evolved', () => new QuakeSkillEffect({
      statusEffects,
      targeting: { getEnemiesInRadius: getEnemiesInRadiusByPx },
      player: { breakHide },
      combat: { applyDamage },
    }));

    this.register('earth_shared_passive_1', () => new BedrockSkillEffect({
      stationary,
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
    }));

    this.register('earth_shared_passive_2', () => new TremorSenseSkillEffect({
      stationary,
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
    }));

    // ── Stone Fist path ────────────────────────────────────────────────────

    this.register('stone_fist', () => new StoneFistSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
        hasEffect: (s, k) => resolver.hasEffect(s, k),
      },
      statusEffects,
      targeting: { getSingleTarget, getConeTargets },
      combat: { applyDamage },
      stationary,
    }));

    this.register('stone_fist_evolved', () => new HammerSkillEffect({
      resolver: { hasEffect: (s, k) => resolver.hasEffect(s, k) },
      targeting: { getSingleTarget, getImpactPosition },
      combat: { applyDamage },
      zones: { create: (config) => zones.create(config) },
    }));

    this.register('stone_fist_path_passive', () => new AftershockSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
      zones: { create: (config) => zones.create(config) },
    }));

    // ── Rupture sub-path ───────────────────────────────────────────────────

    this.register('rupture', () => new RuptureSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        hasEffect: (s, k) => resolver.hasEffect(s, k),
      },
      player: { getFacingAngle: () => player.sprite.angle },
      targeting: { getLineTargets },
      combat: { applyDamage },
      statusEffects,
      zones: { create: (config) => zones.create(config) },
    }));

    // ── Crater sub-path ────────────────────────────────────────────────────

    this.register('crater_drop', () => new CraterDropSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        hasEffect: (s, k) => resolver.hasEffect(s, k),
      },
      targeting: {
        getTargetPosition,
        getEnemiesInRadius: getEnemiesInRadiusByPos,
      },
      combat: { applyDamage },
      zones: { create: (config) => zones.create(config) },
    }));

    this.register('crater_passive', () => new IronThresholdSkillEffect({
      zones: { isInsideOwnTerrain: zones.isInsideOwnTerrain },
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
    }));

    // ── Terraform apotheosis ───────────────────────────────────────────────

    this.register('terraform', () => new TerraformSkillEffect({
      player: { getPosition: getPlayerPosition },
      terrain: { place: (config) => terrainAdapter.place(config) },
      pathfinding,
    }));

    // ── Wall path ──────────────────────────────────────────────────────────

    this.register('wall', () => new WallSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        hasEffect: (s, k) => resolver.hasEffect(s, k),
      },
      placement: { getWallPlacement: placement.getWallPlacement },
      terrain: {
        place: (config) => terrainAdapter.place(config),
        remove: (id) => { terrainAdapter.remove(id); },
      },
      pathfinding,
    }));

    this.register('wall_evolved', () => new RampartSkillEffect({
      resolver: { hasEffect: (s, k) => resolver.hasEffect(s, k) },
      placement: { getWallPlacement: placement.getWallPlacement },
      terrain: {
        place: (config) => terrainAdapter.place(config),
        remove: (id) => { terrainAdapter.remove(id); },
      },
      pathfinding,
    }));

    this.register('wall_path_passive', () => new DeepRootSkillEffect({
      stationary,
    }));

    // ── Standing Wave sub-path ─────────────────────────────────────────────

    this.register('standing_wave', () => new StandingWaveSkillEffect({
      stationary,
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        hasEffect: (s, k) => resolver.hasEffect(s, k),
        getTreePassiveEffect: (t, k, a) => resolver.getTreePassiveEffect(t, k, a),
      },
      playerState: { get fortifyShieldHp() { return fortifyPlayerState.fortifyShieldHp; } },
      targeting: {
        getEnemiesInRadius: getEnemiesInRadiusByPx,
        getEnemyPosition,
        getPlayerPosition,
      },
      combat: { applyDamage, applyKnockback },
      statusEffects: {
        isDebuffed: (targetId: string): boolean => {
          const enemy = findEnemyByTag(targetId);
          if (!enemy?.statusEffectTracker) return false;
          const tracker = enemy.statusEffectTracker;
          return tracker.has('stagger') || tracker.has('slow')
            || tracker.getAll().some(e => e.def.category === 'debuff');
        },
      },
      zones: { create: (config) => zones.create(config) },
    }));

    // ── Fortify sub-path ───────────────────────────────────────────────────

    this.register('fortify', () => new FortifySkillEffect({
      stationary,
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
        hasEffect: (s, k) => resolver.hasEffect(s, k),
      },
      playerState: fortifyPlayerState,
      targeting: {
        getEnemiesInRadius: getEnemiesInRadiusByPx,
        getPlayerPosition,
        getEnemyPosition,
      },
      combat: { applyDamage, applyKnockback },
    }));

    this.register('fortify_passive', () => new WeightBearingSkillEffect({
      stationary,
      terrain: { getAll: () => terrainAdapter.getAll() },
    }));

    // ── Monolith apotheosis ────────────────────────────────────────────────

    this.register('monolith', () => new MonolithSkillEffect({
      player: { getPosition: getPlayerPosition },
      terrain: {
        place: (config) => terrainAdapter.place(config),
        getAll: () => terrainAdapter.getAll(),
      },
      pathfinding,
      targeting: {
        getEnemiesInRadius: getEnemiesInRadiusByPx,
        getEnemyPosition,
      },
      combat: { applyDamage, applyKnockback },
      cooldownHooks,
    }));

    // ── Sculptor path ──────────────────────────────────────────────────────

    this.register('sculptor', () => new SculptorSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
      placement: { getFormationPlacement: placement.getFormationPlacement },
      terrain: {
        place: (config) => terrainAdapter.place(config),
        remove: (id) => { terrainAdapter.remove(id); },
      },
      pathfinding,
    }));

    this.register('sculptor_evolved', () => new PillarSkillEffect({
      targeting: { getTargetedEnemy },
      statusEffects: {
        apply: (effectType: string, params: { durationMs: number; sourceSkillId: string }, targetId: string) => {
          const def = statusEffectSystem.getDef(effectType);
          if (!def) return true;
          const target = findEnemyByTag(targetId);
          target?.statusEffectTracker?.apply(effectType, {
            sourceSkillId: params.sourceSkillId,
            durationMs: params.durationMs,
          });
          return true;
        },
      },
      terrain: {
        place: (config) => terrainAdapter.place(config),
        remove: (id) => { terrainAdapter.remove(id); },
      },
      pathfinding,
    }));

    this.register('sculptor_path_passive', () => new OvergrownSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
      statusEffects: {
        apply: (effectType: string, params: { durationMs: number; intensity: number; sourceSkillId: string }, targetId: string) => {
          const def = statusEffectSystem.getDef(effectType);
          if (!def) return;
          const target = findEnemyByTag(targetId);
          target?.statusEffectTracker?.apply(effectType, {
            sourceSkillId: params.sourceSkillId,
            durationMs: params.durationMs,
            intensity: params.intensity,
          });
        },
      },
    }));

    // ── Cairn sub-path ─────────────────────────────────────────────────────

    this.register('cairn', () => new CairnSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
        hasEffect: (s, k) => resolver.hasEffect(s, k),
      },
      targeting: {
        getTargetPoint,
        getEnemiesAtPosition,
        getEnemiesInRadius: getEnemiesInRadiusByPos,
        getPlayerPosition,
      },
      terrain: {
        place: (config) => terrainAdapter.place(config),
        remove: (id) => { terrainAdapter.remove(id); },
        getActiveCountInRadius: terrainAdapter.getActiveCountInRadius,
        getFormationsInRadius: terrainAdapter.getFormationsInRadius,
        getActiveCount: terrainAdapter.getActiveCount,
      },
      zones: {
        create: (config) => zones.create(config),
        remove: (zoneId) => { zones.remove(zoneId); },
      },
      pathfinding,
      combat: { applyDamage },
    }));

    // ── Sinkhole sub-path ──────────────────────────────────────────────────

    this.register('sinkhole', () => new SinkholeSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        hasEffect: (s, k) => resolver.hasEffect(s, k),
      },
      targeting: {
        getTargetPoint: (rangeTiles) => getTargetPoint(rangeTiles),
        getEnemiesInRadius: getEnemiesInRadiusByPos,
      },
      statusEffects: {
        apply: (targetId: string, type: string, config: { durationMs: number; sourceSkillId: string }) => {
          const def = statusEffectSystem.getDef(type);
          if (!def) return;
          findEnemyByTag(targetId)?.statusEffectTracker?.apply(type, {
            sourceSkillId: config.sourceSkillId,
            durationMs: config.durationMs,
          });
        },
        isSlowedByExternalSource: (targetId: string): boolean => {
          const enemy = findEnemyByTag(targetId);
          // Check for any slow-category status effect
          return enemy?.statusEffectTracker?.has('slow') ?? false;
        },
      },
      zones: { create: (config) => zones.create(config) },
      combat: { applyDamage },
    }));

    // ── Petrify apotheosis ─────────────────────────────────────────────────

    this.register('petrify', () => new PetrifySkillEffect({
      player: { getPosition: getPlayerPosition },
      targeting: {
        getEnemiesInRadius: getEnemiesInRadiusByPx,
        getEnemyPosition,
      },
      combat: { applyDamage, applyKnockback },
      playerState: petrifyPlayerState,
    }));
  }

  /** Register all Conjuration tree active/evolution/passive/apotheosis skills. */
  private _registerConjurationSkills(): void {
    const { player, enemyManager, upgradeResolver: resolver, summonSystem, zoneSystem, deathTriggerSystem, groundLayer } = this.deps;

    // ── Shared adapters ───────────────────────────────────────────────────

    // Progression helpers — enumerate trees via ConfigLoader, query XP via SkillProgressionComponent
    const getNonConjurationTreeNames = () =>
      Object.keys(ConfigLoader.v4Trees).filter(t => t !== 'conjuration');

    const getHighestNonConjurationTree = (): string | null => {
      const trees = getNonConjurationTreeNames();
      let best: string | null = null;
      let bestXp = -1;
      for (const t of trees) {
        const xp = player.skillProgression.getTreeXp(t);
        if (xp > bestXp) { bestXp = xp; best = t; }
      }
      return best;
    };

    const getRandomNonConjurationTrees = (count: number): string[] => {
      const trees = getNonConjurationTreeNames();
      const shuffled = [...trees].sort(() => Math.random() - 0.5);
      return shuffled.slice(0, count);
    };

    // STUB: deferred — no CombatComponent.baseDamage yet; hardcoded until combat stats are wired
    const getBaseDamage = () => 10;

    const getPlayerPosition = () => ({ x: player.sprite.x, y: player.sprite.y });
    const getPlayerFacing = () => {
      const vel = player.body.velocity;
      const mag = Math.sqrt(vel.x * vel.x + vel.y * vel.y);
      return mag > 0.1 ? { x: vel.x / mag, y: vel.y / mag } : { x: 0, y: 1 };
    };

    const collision = {
      isWallAt: (x: number, y: number): boolean => {
        const tileX = Math.floor(x / TILE_SIZE);
        const tileY = Math.floor(y / TILE_SIZE);
        return tileBlocksMovement(groundLayer.getTileAt(tileX, tileY));
      },
    };

    const targeting = {
      shouldFizzle: () => !enemyManager.all.some(e => !e.healthComponent.isDead),
    };

    // Summon adapter bridging SummonSystem for wisp operations.
    // Skill effects define their own config types (WispSummonConfig etc.) — cast through
    // unknown at the boundary since SummonSystem.spawn() expects SummonConfig.
    const wispSummons = {
      spawn: (config: unknown, x: number, y: number) =>
        summonSystem.spawn(config as SummonConfig, x, y).instanceId,
      getWispCount: () => summonSystem.count,
      getMaxWisps: () => 5, // STUB: deferred — cap management wired by wisp AI layer
      commandDetonate: () => { summonSystem.commandDetonate(1, 0); },
      commandConverge: (
        targetX: number, targetY: number,
        _rushSpeedMultiplier: number, _impactDamage: number, _vanguardStaggerMs: number,
      ) => { summonSystem.commandConverge(targetX, targetY); },
      spawnWisp: (x: number, y: number) => {
        const stub: SummonConfig = {
          id: 'wisp', archetype: 'wisp', baseHp: 10, baseDamage: 0,
          baseSpeed: 60, leashRadiusTiles: 8, aggroWeight: 0.6,
        };
        return summonSystem.spawn(stub, x, y).instanceId;
      },
      getWisps: (): WispData[] => {
        return summonSystem.getAll()
          .filter(s => s.config.archetype === 'wisp')
          .map(s => ({ id: s.instanceId, position: { x: s.x, y: s.y }, spawnTimeMs: 0 }));
      },
      destroyAll: () => { summonSystem.destroyAll(); },
    };

    // Companion summon adapter for Bind/Empower/Chimera/Manifest
    const companionSummons = {
      spawn: (config: unknown, x: number, y: number) =>
        summonSystem.spawn(config as SummonConfig, x, y).instanceId,
    };

    // Shade summon adapter for Tether/Mirror/Fracture/Detach/FractureApotheosis
    const shadeSummons = {
      spawn: (config: unknown, x: number, y: number) =>
        summonSystem.spawn(config as SummonConfig, x, y).instanceId,
      destroy: (instanceId: string) => { summonSystem.destroy(instanceId); },
      getShadeIds: () =>
        summonSystem.getByArchetype('shade').map(s => s.instanceId),
      getShadePosition: (instanceId: string) => {
        const s = summonSystem.get(instanceId);
        return s && !s.isDead ? { x: s.x, y: s.y } : null;
      },
      getShadeHp: (instanceId: string) => summonSystem.get(instanceId)?.hp ?? 0,
      isShadeAlive: (instanceId: string) => {
        const s = summonSystem.get(instanceId);
        return !!s && !s.isDead;
      },
    };

    // Combat adapter for AoE explosions (Expendable, Detonate, HairTrigger, ChainDetonation)
    const combat = {
      applyAoEDamage: (x: number, y: number, radiusTiles: number, damage: number) => {
        // Apply AoE damage to all living enemies within radius
        const radiusPx = radiusTiles * TILE_SIZE;
        const radiusSq = radiusPx * radiusPx;
        for (const enemy of this.deps.enemyManager.all) {
          if (enemy.healthComponent.isDead) continue;
          const dx = enemy.sprite.x - x;
          const dy = enemy.sprite.y - y;
          if (dx * dx + dy * dy <= radiusSq) {
            dealDamageToEnemy(enemy, damage);
          }
        }

        // Aggro leak: when player is concealed and summons deal AoE damage,
        // bleed a percentage of the damage as aggro toward the player (ADR-073).
        const summonsCfg = ConfigLoader.combat.summons;
        const playerConcealed = player.skillActivation.isSkillActive('hide')
          || player.skillActivation.isSkillActive('stealth');
        if (!summonsCfg.aggroLeakRequiresHide || playerConcealed) {
          const radiusPx = radiusTiles * TILE_SIZE;
          const radiusSq = radiusPx * radiusPx;
          const leakAmount = damage * summonsCfg.aggroLeakPct;
          for (const enemy of this.deps.enemyManager.all) {
            if (enemy.healthComponent.isDead) continue;
            const dx = enemy.sprite.x - x;
            const dy = enemy.sprite.y - y;
            if (dx * dx + dy * dy <= radiusSq) {
              enemy.ai.raiseAlert(leakAmount);
            }
          }
        }
      },
    };

    // Clock adapter for Detonate lifespan calculations
    const clock = { nowMs: () => Date.now() };

    // Enemies adapter for Overwhelm target assignment and Severance radius queries
    const enemies = {
      getAll: () => this.deps.enemyManager.all.map(e => ({
        id: e.instanceTag,
        position: { x: e.sprite.x, y: e.sprite.y },
      })),
      getInRadius: (x: number, y: number, radiusTiles: number) => {
        const radiusPx = radiusTiles * TILE_SIZE;
        return this.deps.enemyManager.all
          .filter(e => {
            const dx = e.sprite.x - x;
            const dy = e.sprite.y - y;
            return dx * dx + dy * dy <= radiusPx * radiusPx;
          })
          .map(e => ({ id: e.instanceTag }));
      },
    };

    // Status effects adapter for Severance (wired to real StatusEffectTracker)
    const findEnemyByTag = (tag: string) =>
      this.deps.enemyManager.all.find(e => e.instanceTag === tag && !e.healthComponent.isDead);
    const statusEffects = {
      applyToTarget: (targetId: string, type: string, opts: {
        durationMs: number; intensity?: number; sourceSkillId: string;
      }): boolean => {
        const def = this.deps.statusEffectSystem.getDef(type);
        if (!def) return false;
        const enemy = findEnemyByTag(targetId);
        if (!enemy?.statusEffectTracker) return false;
        enemy.statusEffectTracker.apply(type, {
          sourceSkillId: opts.sourceSkillId,
          durationMs: opts.durationMs,
          intensity: opts.intensity,
        });
        return true;
      },
      isTargetImmune: (targetId: string, statusId: string): boolean =>
        findEnemyByTag(targetId)?.statusEffectTracker?.isImmune(statusId) ?? false,
    };

    // ── Trunk ─────────────────────────────────────────────────────────────

    this.register('summon', () => new SummonSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k, b) => resolver.getMultiplicativeEffect(s, k, b),
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
      },
      player: { getPosition: getPlayerPosition, getFacingDirection: getPlayerFacing },
      collision,
      summons: wispSummons,
    }));

    this.register('invoke', () => new InvokeSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k, b) => resolver.getMultiplicativeEffect(s, k, b),
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
      },
      player: { getPosition: getPlayerPosition, getFacingDirection: getPlayerFacing },
      collision,
      targeting,
      summons: wispSummons,
    }));

    this.register('bond', () => new BondSkillEffect({
      resolver: {
        getTreePassiveEffect: (tree, key, agg) =>
          resolver.getTreePassiveEffect(tree, key, agg as 'sum' | 'product' | 'max' | 'min'),
      },
    }));

    this.register('vestige', () => new VestigeSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
      zones: {
        create: (config) => zoneSystem.create(config),
      },
    }));

    // ── Swarm path ────────────────────────────────────────────────────────

    this.register('swarm', () => new SwarmSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k, b) => resolver.getMultiplicativeEffect(s, k, b),
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
      },
      player: { getPosition: getPlayerPosition, getFacingDirection: getPlayerFacing },
      collision,
      summons: wispSummons,
    }));

    this.register('overwhelm', () => new OverwhelmSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k, b) => resolver.getMultiplicativeEffect(s, k, b),
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
      },
      player: { getPosition: getPlayerPosition, getFacingDirection: getPlayerFacing },
      collision,
      targeting,
      summons: wispSummons,
      enemies,
    }));

    this.register('swarm_path_passive', () => new ExpendableSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
      },
      combat,
    }));

    // ── Detonate sub-path ─────────────────────────────────────────────────

    this.register('detonate', () => new DetonateSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k, b) => resolver.getMultiplicativeEffect(s, k, b),
      },
      summons: {
        getWisps: wispSummons.getWisps,
        commandDetonate: wispSummons.commandDetonate,
      },
      combat,
      clock,
    }));

    this.register('detonate_upgrade_2', () => new ChainDetonationSkillEffect({
      deathTriggers: {
        registerTrigger: (params) => {
          deathTriggerSystem.register({
            id: 'chain_detonation',
            sourceSkillId: params.requireKillBySkill,
            explosion: { damage: 0, radiusTiles: 0 },
          });
        },
      },
      combat,
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k) => resolver.getMultiplicativeEffect(s, k),
      },
    }));

    this.register('detonate_passive', () => new HairTriggerSkillEffect({
      combat,
    }));

    // ── Convergence sub-path ──────────────────────────────────────────────

    this.register('convergence', () => new ConvergenceSkillEffect({
      resolver: {
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMinEffect: (s, k) => resolver.getMinEffect(s, k),
      },
      summons: {
        getWispCount: wispSummons.getWispCount,
        getMaxWisps: wispSummons.getMaxWisps,
        commandConverge: wispSummons.commandConverge,
        spawnWisp: wispSummons.spawnWisp,
      },
      player: { getPosition: getPlayerPosition },
      targeting: {
        getNearestEnemy: () => {
          const all = this.deps.enemyManager.all;
          if (all.length === 0) return null;
          const px = player.sprite.x;
          const py = player.sprite.y;
          let nearest = all[0];
          let bestDist = Infinity;
          for (const e of all) {
            const dx = e.sprite.x - px;
            const dy = e.sprite.y - py;
            const dist = dx * dx + dy * dy;
            if (dist < bestDist) { bestDist = dist; nearest = e; }
          }
          return { x: nearest.sprite.x, y: nearest.sprite.y };
        },
      },
    }));

    this.register('convergence_passive', () => new RelentlessSkillEffect({
      resolver: {
        getMinEffect: (s, k) => resolver.getMinEffect(s, k),
      },
      summons: {
        getWispCount: wispSummons.getWispCount,
        getMaxWisps: wispSummons.getMaxWisps,
        spawnWisp: wispSummons.spawnWisp,
      },
      player: { getPosition: getPlayerPosition },
    }));

    // ── Swarm apotheosis ──────────────────────────────────────────────────

    this.register('becomes_the_many', () => new BecomesTheManySkillEffect({
      resolver: {
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
        getMultiplicativeEffect: (s, k, b) => resolver.getMultiplicativeEffect(s, k, b),
      },
      player: { getPosition: getPlayerPosition },
      summons: wispSummons,
      targeting,
    }));

    // ── Bind path ─────────────────────────────────────────────────────────

    this.register('bind', () => new BindSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        hasEffect: (s, k) => resolver.hasEffect(s, k),
        getMinEffect: (s, k) => resolver.getMinEffect(s, k),
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
      },
      player: { getPosition: getPlayerPosition },
      targeting,
      summons: companionSummons,
      progression: {
        getHighestNonConjurationTree,
      },
    }));

    this.register('empower', () => new EmpowerSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        hasEffect: (s, k) => resolver.hasEffect(s, k),
        getMinEffect: (s, k) => resolver.getMinEffect(s, k),
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
      },
      player: { getPosition: getPlayerPosition },
      targeting,
      summons: companionSummons,
      progression: {
        getHighestNonConjurationTree,
      },
    }));

    this.register('bind_path_passive', () => new DevotedSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
    }));

    // ── Chimera sub-path ──────────────────────────────────────────────────

    this.register('chimera', () => new ChimeraSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k) => resolver.getMultiplicativeEffect(s, k),
        hasEffect: (s, k) => resolver.hasEffect(s, k),
        getMinEffect: (s, k) => resolver.getMinEffect(s, k),
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
      },
      player: { getPosition: getPlayerPosition },
      targeting,
      summons: companionSummons,
      progression: {
        getRandomNonConjurationTrees,
      },
    }));

    this.register('chimera_passive', () => new BondedSkillEffect({
      resolver: {
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
      },
    }));

    // ── Revival sub-path ──────────────────────────────────────────────────

    this.register('revival', () => new RevivalSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k) => resolver.getMultiplicativeEffect(s, k),
        hasEffect: (s, k) => resolver.hasEffect(s, k),
        getMinEffect: (s, k) => resolver.getMinEffect(s, k),
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
      },
      summons: companionSummons,
    }));

    this.register('revival_passive', () => new AnchoredSkillEffect({
      resolver: {
        getMinEffect: (s, k) => resolver.getMinEffect(s, k),
      },
    }));

    // ── Bind apotheosis ───────────────────────────────────────────────────

    this.register('manifest', () => new ManifestSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        hasEffect: (s, k) => resolver.hasEffect(s, k),
        getMinEffect: (s, k) => resolver.getMinEffect(s, k),
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
      },
      player: { getPosition: getPlayerPosition },
      targeting,
      summons: companionSummons,
      progression: {
        getHighestNonConjurationTree,
      },
    }));

    // ── Tether path ───────────────────────────────────────────────────────

    this.register('tether_clarity', () => new ClaritySkillEffect());

    this.register('tether_synapse', () => new SynapseSkillEffect());

    this.register('tether', () => new TetherSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
      },
      player: {
        getPosition: getPlayerPosition,
        getFacingDirection: getPlayerFacing,
        getBaseDamage,
      },
      targeting,
      summons: shadeSummons,
      getMirrorDistanceTiles: () => {
        const clarity = player.skillActivation.getEffect<ClaritySkillEffect>('tether_clarity');
        return clarity?.getCurrentMirrorDistanceTiles() ?? 3;
      },
    }));

    this.register('mirror', () => new MirrorSkillEffect({
      resolver: {
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
      },
      player: {
        getPosition: getPlayerPosition,
        getBaseDamage,
      },
      targeting,
      summons: shadeSummons,
    }));

    this.register('tether_path_passive', () => new RecursiveSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k) => resolver.getMultiplicativeEffect(s, k),
      },
    }));

    // ── Fracture sub-path ─────────────────────────────────────────────────

    this.register('fracture', () => new FractureSkillEffect({
      resolver: {
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
        getMultiplicativeEffect: (s, k) => resolver.getMultiplicativeEffect(s, k),
      },
      player: {
        getPosition: getPlayerPosition,
        getFacingDirection: getPlayerFacing,
        getBaseDamage,
      },
      targeting,
      summons: shadeSummons,
      getMirrorDistanceTiles: () => {
        const clarity = player.skillActivation.getEffect<ClaritySkillEffect>('tether_clarity');
        return clarity?.getCurrentMirrorDistanceTiles() ?? 3;
      },
    }));

    this.register('fracture_upgrade_2', () => new ResonantFormationSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
      },
    }));

    this.register('fracture_passive', () => new UnmooredSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
      },
    }));

    // ── Detach sub-path ───────────────────────────────────────────────────

    this.register('detach', () => new DetachSkillEffect({
      resolver: {
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
      },
      player: {
        getPosition: getPlayerPosition,
        getBaseDamage,
        getCurrentHp: () => player.health.hp,
        deductHp: (amount) => { player.health.setHp(player.health.hp - amount); },
        heal: (amount) => { player.health.heal(amount); },
      },
      targeting,
      summons: shadeSummons,
    }));

    this.register('detach_upgrade_2', () => new SeveranceSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
      enemies,
      statusEffects,
    }));

    this.register('detach_passive', () => new DetachBarrierPassiveSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
      },
    }));

    // ── Tether apotheosis ─────────────────────────────────────────────────

    this.register('fracture_apotheosis', () => new FractureApotheosisSkillEffect({
      resolver: {
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
      },
      player: {
        getPosition: getPlayerPosition,
        getFacingDirection: getPlayerFacing,
        getMaxHp: () => player.health.max,
        getBaseDamage,
        heal: (amount) => { player.health.heal(amount); },
      },
      targeting,
      summons: shadeSummons,
    }));
  }

  /** Register all Astral tree skill effects. Blink is the foundation (no skip). */
  private _registerAstralSkills(): void {
    const {
      player,
      enemyManager,
      upgradeResolver: resolver,
      astralTeleportSystem,
      foresightRenderSystem,
      gravityPullSystem,
      orbitalDebrisSystem,
      temporalEchoStateSystem,
      astralProjectionStateSystem,
      zoneSystem,
      cooldownHookSystem,
      deathTriggerSystem,
      knockbackSystem,
    } = this.deps;

    // ── Shared adapters ──────────────────────────────────────────────────

    const getPlayerPosition = (): { x: number; y: number } => ({
      x: player.sprite.x, y: player.sprite.y,
    });

    // Targeting — real closure against enemyManager
    const targeting = {
      getTargetPosition: (): { x: number; y: number } => {
        const alive = enemyManager.all.find(e => !e.healthComponent.isDead);
        return alive
          ? { x: alive.sprite.x, y: alive.sprite.y }
          : { x: player.sprite.x, y: player.sprite.y };
      },
    };

    // Status-effects adapter (wired to real StatusEffectTracker)
    const { statusEffectSystem } = this.deps;
    const findEnemyByTag = (tag: string) =>
      enemyManager.all.find(e => e.instanceTag === tag && !e.healthComponent.isDead);
    const nearestAliveEnemy = (maxRangePx: number) => {
      let best: import('../entities/Enemy').Enemy | null = null;
      let bestDistSq = maxRangePx * maxRangePx;
      const px = player.sprite.x, py = player.sprite.y;
      for (const e of enemyManager.all) {
        if (e.healthComponent.isDead) continue;
        const dx = e.sprite.x - px, dy = e.sprite.y - py;
        const dSq = dx * dx + dy * dy;
        if (dSq < bestDistSq) { bestDistSq = dSq; best = e; }
      }
      return best;
    };
    const statusEffects = {
      apply: (effectId: string, ...args: unknown[]) => {
        const def = statusEffectSystem.getDef(effectId);
        if (!def) return;
        // Parse variadic args: may include targetEntityId (string) and durationMs (number)
        let targetId: string | undefined;
        let durationMs: number | undefined;
        for (const arg of args) {
          if (typeof arg === 'string') targetId = arg;
          else if (typeof arg === 'number') durationMs = arg;
        }
        const applyOpts = { sourceSkillId: 'astral', durationMs };
        if (targetId) {
          findEnemyByTag(targetId)?.statusEffectTracker?.apply(effectId, applyOpts);
        } else {
          const target = nearestAliveEnemy(2 * TILE_SIZE);
          target?.statusEffectTracker?.apply(effectId, applyOpts);
        }
      },
      remove: (statusId: string, ...args: unknown[]) => {
        const targetId = args.find(a => typeof a === 'string') as string | undefined;
        if (targetId) {
          findEnemyByTag(targetId)?.statusEffectTracker?.remove(statusId);
        } else {
          for (const e of enemyManager.all) {
            if (!e.healthComponent.isDead) e.statusEffectTracker?.remove(statusId);
          }
        }
      },
      isImmune: (statusId: string): boolean =>
        nearestAliveEnemy(2 * TILE_SIZE)?.statusEffectTracker?.isImmune(statusId) ?? false,
      applyAstralMark: (enemyId: string) => {
        const def = statusEffectSystem.getDef('astral_mark');
        if (!def) return;
        // STUB: deferred — no on-hit mark consumption system for bonus damage
        findEnemyByTag(enemyId)?.statusEffectTracker?.apply('astral_mark', {
          sourceSkillId: 'astral_projection',
        });
      },
      applyHaunted: (enemyId: string, durationMs: number) => {
        const def = statusEffectSystem.getDef('haunted');
        if (!def) return;
        // STUB: deferred — no enemy AI haunted behavior implemented
        findEnemyByTag(enemyId)?.statusEffectTracker?.apply('haunted', {
          sourceSkillId: 'haunted_passive',
          durationMs,
        });
      },
      removeHaunted: (enemyId: string) => {
        findEnemyByTag(enemyId)?.statusEffectTracker?.remove('haunted');
      },
    };

    // Enemy queries — real closures against enemyManager
    const getEnemiesInRadius = (
      xOrPos: number | { x: number; y: number },
      yOrRadius?: number,
      radiusTiles?: number,
    ): string[] => {
      let cx: number, cy: number, rTiles: number;
      if (typeof xOrPos === 'object') {
        cx = xOrPos.x; cy = xOrPos.y; rTiles = yOrRadius ?? 1;
      } else {
        cx = xOrPos; cy = yOrRadius ?? 0; rTiles = radiusTiles ?? 1;
      }
      const maxDist = rTiles * TILE_SIZE;
      return enemyManager.all
        .filter(e => {
          if (e.healthComponent.isDead) return false;
          const dx = e.sprite.x - cx;
          const dy = e.sprite.y - cy;
          return Math.sqrt(dx * dx + dy * dy) <= maxDist;
        })
        .map(e => e.instanceTag);
    };

    // Combat — real closures against enemyManager
    const combat = {
      dealDamage: (enemyId: string, damage: number) => {
        const target = enemyManager.all.find(e => e.instanceTag === enemyId);
        if (target && !target.healthComponent.isDead) {
          dealDamageToEnemy(target, damage);
        }
      },
      applyStatusEffect: (
        entityId: string, defId: string,
        opts: { durationMs: number; sourceSkillId: string },
      ): boolean => {
        const def = statusEffectSystem.getDef(defId);
        if (!def) return false;
        const enemy = findEnemyByTag(entityId);
        if (!enemy?.statusEffectTracker) return false;
        enemy.statusEffectTracker.apply(defId, {
          sourceSkillId: opts.sourceSkillId,
          durationMs: opts.durationMs,
        });
        return true;
      },
    };

    // Knockback — wired to KnockbackSystem
    const knockback = {
      apply: (tiles: number) => {
        const px = player.sprite.x, py = player.sprite.y;
        const radiusPx = 2 * TILE_SIZE;
        const distPx = tiles * TILE_SIZE;
        const config = ConfigLoader.combat.knockback;
        for (const e of enemyManager.all) {
          if (e.healthComponent.isDead) continue;
          const dx = e.sprite.x - px, dy = e.sprite.y - py;
          const distSq = dx * dx + dy * dy;
          if (distSq > radiusPx * radiusPx) continue;
          const len = Math.sqrt(distSq) || 1;
          knockbackSystem.applyRaw(e, { x: dx / len, y: dy / len }, distPx * config.velocityMultiplier, config.durationMs);
        }
      },
      pushFromPoint: (enemyId: string, center: { x: number; y: number }) => {
        const enemy = findEnemyByTag(enemyId);
        if (!enemy) return;
        const dx = enemy.sprite.x - center.x;
        const dy = enemy.sprite.y - center.y;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        const config = ConfigLoader.combat.knockback;
        knockbackSystem.applyRaw(enemy, { x: dx / len, y: dy / len }, config.baseDistance * config.velocityMultiplier, config.durationMs);
      },
    };

    // Movement lock — delegates to PlayerStateComponent
    const movement = {
      lock: () => { player.playerState.setMovementLocked('astral_channel', true); },
      unlock: () => { player.playerState.setMovementLocked('astral_channel', false); },
    };

    // Zone adapter — bridges to ZoneSystem
    const zones = {
      create: (config: unknown): string =>
        zoneSystem.create(config as Parameters<typeof zoneSystem.create>[0]),
      remove: (zoneId: string) => { zoneSystem.remove(zoneId); },
      isInsideType: (x: number, y: number, zoneType: string): boolean =>
        zoneSystem.isInsideType(x, y, zoneType),
    };

    // ── Trunk ────────────────────────────────────────────────────────────

    this.register('blink', () => new BlinkSkillEffect({
      resolver: {
        getMultiplicativeEffect: (s, k) => resolver.getMultiplicativeEffect(s, k),
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
      teleport: astralTeleportSystem,
      player: { getPosition: getPlayerPosition },
      targeting,
      xpSource: {
        awardTeleportXp: (dangerPresent: boolean) => {
          player.skillProgression.recordUse('blink', { danger: dangerPresent ? this.getDanger() : 0 });
        },
        isDangerPresent: () => this.getDanger() > 0,
      },
    }));

    this.register('phase_step', () => new PhaseStepSkillEffect({
      resolver: {
        getMultiplicativeEffect: (s, k) => resolver.getMultiplicativeEffect(s, k),
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
      teleport: astralTeleportSystem,
      player: { getPosition: getPlayerPosition },
      targeting,
      xpSource: {
        awardTeleportXp: (dangerPresent: boolean) => {
          player.skillProgression.recordUse('phase_step', { danger: dangerPresent ? this.getDanger() : 0 });
        },
        isDangerPresent: () => this.getDanger() > 0,
      },
    }));

    this.register('spatial_memory', () => new SpatialMemorySkillEffect({
      teleport: astralTeleportSystem,
      renderer: undefined, // STUB: deferred — spatial memory render system not yet built
    }));

    this.register('foresight_passive', () => new ForesightPassiveSkillEffect({
      renderer: foresightRenderSystem,
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
      temporalEcho: temporalEchoStateSystem,
    }));

    // ── Rift path ────────────────────────────────────────────────────────

    this.register('rift', () => new RiftSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
      gravityPull: gravityPullSystem,
      zones,
      targeting,
      player: { getPosition: getPlayerPosition },
    }));

    this.register('implosion', () => new ImplosionSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
      gravityPull: gravityPullSystem,
      zones,
      targeting,
      player: { getPosition: getPlayerPosition },
      enemies: {
        getEnemiesInRadius: (x: number, y: number, r: number) => getEnemiesInRadius(x, y, r),
      },
      combat,
      cooldownHooks: cooldownHookSystem,
    }));

    this.register('rift_path_passive', () => new FoldedSpacePassiveSkillEffect({
      cooldownHooks: cooldownHookSystem,
    }));

    // ── Event Horizon sub-path ───────────────────────────────────────────

    this.register('event_horizon', () => new EventHorizonSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        getMultiplicativeEffect: (s, k) => resolver.getMultiplicativeEffect(s, k),
      },
      zones,
      player: { getPosition: getPlayerPosition },
    }));

    this.register('event_horizon_passive', () => new SingularityPassiveSkillEffect({
      zones,
      enemies: {
        getEnemiesInRadius: (x: number, y: number, r: number) => getEnemiesInRadius(x, y, r),
      },
      combat,
      rng: { random: () => Math.random() },
    }));

    // ── Anchor sub-path ──────────────────────────────────────────────────

    this.register('anchor', () => new AnchorSkillEffect({
      resolver: {
        getMaxEffect: (s, k) => resolver.getMaxEffect(s, k),
      },
      gravityPull: {
        addWell: (config) => gravityPullSystem.addWell(config),
        removeWell: (id) => { gravityPullSystem.removeWell(id); },
        getAll: () => gravityPullSystem.getAll(),
        onWellExpired: (cb) => {
          gravityPullSystem.onWellExpired?.(cb);
          return () => { /* unsubscribe handled by system destroy */ };
        },
      },
      targeting,
      player: { getPosition: getPlayerPosition },
      enemies: {
        getEnemiesInRadius: (pos: { x: number; y: number }, r: number) =>
          getEnemiesInRadius(pos, r),
      },
      combat,
    }));

    this.register('anchor_passive', () => new WarpGeometryPassiveSkillEffect({
      teleport: {
        onTeleport: (cb) => { astralTeleportSystem.onTeleport(cb); },
      },
      anchorSource: {
        getActiveWells: () => {
          const anchor = player.skillActivation.getEffect<AnchorSkillEffect>('anchor');
          if (!anchor) return [];
          // Delegate to GravityPullSystem for active wells from this skill
          return gravityPullSystem.getAll()
            .filter(w => w.sourceSkillId === 'anchor')
            .map(w => ({ position: w.position, pullRadiusTiles: w.pullRadiusTiles }));
        },
      },
      enemies: {
        getEnemiesInRadius: (center: { x: number; y: number }, r: number) =>
          getEnemiesInRadius(center, r),
      },
      knockback,
    }));

    // ── Rift path apotheosis ─────────────────────────────────────────────

    this.register('apotheosis_event_horizon', () => new DeadStarSkillEffect({
      gravityPull: {
        addWell: (config) => gravityPullSystem.addWell(config),
        removeWell: (id) => { gravityPullSystem.removeWell(id); },
      },
      zones,
      enemies: {
        getEnemiesInRadius: (pos: { x: number; y: number }, r: number) =>
          getEnemiesInRadius(pos, r),
        getEnemyPosition: (enemyId: string) => {
          const e = enemyManager.all.find(en => en.instanceTag === enemyId);
          return e && !e.healthComponent.isDead ? { x: e.sprite.x, y: e.sprite.y } : null;
        },
      },
      combat,
      targeting,
      player: { getPosition: getPlayerPosition },
    }));

    // ── Oracle path ──────────────────────────────────────────────────────

    this.register('oracle', () => new OracleSkillEffect({
      renderer: foresightRenderSystem,
      teleport: astralTeleportSystem,
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
      enemies: {
        getAllEnemies: () => this.deps.enemyManager.all,
      },
    }));

    this.register('prescience', () => new PrescienceSkillEffect({
      renderer: foresightRenderSystem,
      teleport: astralTeleportSystem,
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
      enemies: {
        getAllEnemies: () => this.deps.enemyManager.all,
      },
      timeController: {
        setTimeScale: (scale: number) => {
          this.deps.cameras.main.scene.time.timeScale = scale;
        },
        getTimeScale: () => this.deps.cameras.main.scene.time.timeScale,
      },
    }));

    this.register('oracle_path_passive', () => new SecondSightPassiveSkillEffect());

    // ── Astral Projection sub-path ───────────────────────────────────────

    this.register('astral_projection', () => new AstralProjectionSkillEffect({
      projection: astralProjectionStateSystem,
      foresight: {
        showExtendedArcs: (entityIds, durationMs) => {
          foresightRenderSystem.showExtendedArcs?.(entityIds, durationMs);
        },
      },
      statusEffects: {
        applyAstralMark: statusEffects.applyAstralMark,
      },
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
      playerPosition: getPlayerPosition,
    }));

    this.register('astral_projection_passive', () => new HauntedPassiveSkillEffect({
      statusEffects: {
        applyHaunted: statusEffects.applyHaunted,
        removeHaunted: statusEffects.removeHaunted,
      },
    }));

    // ── Temporal Echo sub-path ───────────────────────────────────────────

    this.register('temporal_echo', () => new TemporalEchoSkillEffect({
      temporalEcho: temporalEchoStateSystem,
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
      cooldownHooks: cooldownHookSystem,
      playerState: () => ({
        x: player.sprite.x,
        y: player.sprite.y,
        hp: player.health.hp,
      }),
    }));

    this.register('temporal_echo_upgrade_2', () => new PreservedStateSkillEffect({
      cooldownHooks: cooldownHookSystem,
    }));

    this.register('temporal_echo_passive', () => new ForeshadowPassiveSkillEffect());

    // ── Oracle path apotheosis ───────────────────────────────────────────

    this.register('apotheosis_astral_projection', () => new GhostProtocolSkillEffect({
      ghostForm: {
        dematerialize: () => {
          player.playerState.setGhostForm('ghost_protocol', true);
          // TODO: physics intangibility + LOS hiding deferred until intangibility system exists
        },
        rematerialize: () => {
          player.playerState.setGhostForm('ghost_protocol', false);
        },
      },
      statusEffects: {
        apply: (effectId, targetId, durationMs) => {
          statusEffects.apply(effectId, targetId, durationMs);
        },
      },
      oracleSkill: {
        activateForFree: (): boolean => {
          const prescience = player.skillActivation.getEffect<PrescienceSkillEffect>('prescience');
          if (prescience) {
            return (prescience as unknown as { activateForFree(): boolean }).activateForFree();
          }
          const oracle = player.skillActivation.getEffect<OracleSkillEffect>('oracle');
          if (oracle) {
            return (oracle as unknown as { activateForFree(): boolean }).activateForFree();
          }
          return false;
        },
      },
    }));

    // ── Dense path ───────────────────────────────────────────────────────

    this.register('dense', () => new DenseSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        hasEffect: (s, k) => resolver.hasEffect(s, k),
      },
      statusEffects,
      zones,
      teleport: {
        onTeleport: (cb) => { astralTeleportSystem.onTeleport(cb); },
      },
      knockback,
    }));

    this.register('dense_knockback', () => new MassDifferentialPassiveSkillEffect());

    this.register('dense_duration', () => new SustainedMassPassiveSkillEffect());

    this.register('collapse', () => new CollapseSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
        hasEffect: (s, k) => resolver.hasEffect(s, k),
      },
      statusEffects,
      zones,
      teleport: {
        onTeleport: (cb) => { astralTeleportSystem.onTeleport(cb); },
      },
      knockback,
      enemies: {
        getEnemiesInRadius: (x: number, y: number, r: number) => getEnemiesInRadius(x, y, r),
        getNearestEnemy: (radiusTiles: number): string | null => {
          const maxDist = radiusTiles * TILE_SIZE;
          let best: { tag: string; dist: number } | null = null;
          for (const e of enemyManager.all) {
            if (e.healthComponent.isDead) continue;
            const dx = e.sprite.x - player.sprite.x;
            const dy = e.sprite.y - player.sprite.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist <= maxDist && (!best || dist < best.dist)) {
              best = { tag: e.instanceTag, dist };
            }
          }
          return best?.tag ?? null;
        },
        getPosition: (enemyId: string): { x: number; y: number } | null => {
          const e = enemyManager.all.find(en => en.instanceTag === enemyId);
          return e && !e.healthComponent.isDead ? { x: e.sprite.x, y: e.sprite.y } : null;
        },
      },
      combat,
      gravityPull: gravityPullSystem,
    }));

    this.register('dense_path_passive', () => new OrbitalDebrisPassiveSkillEffect({
      collapseSource: {
        isActive: () => {
          const collapse = player.skillActivation.getEffect<CollapseSkillEffect>('collapse')
            ?? player.skillActivation.getEffect<DenseSkillEffect>('dense');
          return collapse?.isActive() ?? false;
        },
      },
      debrisSystem: orbitalDebrisSystem,
      player: { getPosition: getPlayerPosition },
      enemies: {
        getEnemiesInRadius: (x: number, y: number, r: number) => getEnemiesInRadius(x, y, r),
      },
      combat,
      resolver: {
        getMultiplicativeEffect: (s, k, base) => resolver.getMultiplicativeEffect(s, k, base),
      },
    }));

    // ── Meteo sub-path ───────────────────────────────────────────────────

    this.register('meteo', () => new MeteoSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
      zones,
      player: { getPosition: getPlayerPosition },
      movement,
      enemies: {
        getEnemiesInRadius: (pos: { x: number; y: number }, r: number) =>
          getEnemiesInRadius(pos, r),
      },
      combat,
    }));

    this.register('meteo_passive', () => new GravityScarPassiveSkillEffect({
      deathTriggers: {
        register: (config) => { deathTriggerSystem.register(config as Parameters<typeof deathTriggerSystem.register>[0]); },
        unregister: (triggerId) => { deathTriggerSystem.unregister(triggerId); },
      },
    }));

    // ── Slingshot sub-path ───────────────────────────────────────────────

    this.register('slingshot', () => new SlingshotSkillEffect({
      resolver: {
        hasEffect: (s, k) => resolver.hasEffect(s, k),
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
      orbitalDebris: orbitalDebrisSystem,
      statusEffects,
      combat,
      enemies: {
        getPrimaryTarget: (): string | null => {
          let best: { tag: string; dist: number } | null = null;
          for (const e of enemyManager.all) {
            if (e.healthComponent.isDead) continue;
            const dx = e.sprite.x - player.sprite.x;
            const dy = e.sprite.y - player.sprite.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (!best || dist < best.dist) {
              best = { tag: e.instanceTag, dist };
            }
          }
          return best?.tag ?? null;
        },
        getSecondariesInCone: (
          primaryTargetId: string, _angleDegrees: number, maxTargets: number,
        ): string[] => {
          return enemyManager.all
            .filter(e => !e.healthComponent.isDead && e.instanceTag !== primaryTargetId)
            .map(e => e.instanceTag)
            .slice(0, maxTargets);
        },
      },
    }));

    this.register('slingshot_passive', () => new OrbitalMechanicsPassiveSkillEffect({
      displacement: {
        onDisplacementComplete: (_callback) => { /* STUB: deferred — wired when KnockbackSystem emits events */ },
      },
      combat: {
        dealBonusDamage: (enemyId: string, amount: number, _sourceSkillId: string) => {
          const target = findEnemyByTag(enemyId);
          if (target) dealDamageToEnemy(target, amount);
        },
      },
    }));

    // ── Dense path apotheosis ────────────────────────────────────────────

    this.register('apotheosis_meteo', () => new OrbitalBombardmentSkillEffect({
      resolver: {
        getAdditiveEffect: (s, k) => resolver.getAdditiveEffect(s, k),
      },
      zones,
      targeting,
      movement,
      enemies: {
        getEnemiesInRadius: (pos: { x: number; y: number }, r: number) =>
          getEnemiesInRadius(pos, r),
      },
      combat,
    }));
  }

  /** Create and register effects for all currently-unlocked nodes whose tree is selected. */
  private _initialize(): void {
    const selected = new Set(this.deps.player.attunement.getSelectedTrees());
    for (const nodeId of this.registry.keys()) {
      if (!this.deps.player.skillProgression.isUnlocked(nodeId)) continue;
      const tree = ConfigLoader.getV4SkillTree(nodeId);
      if (tree !== undefined && !selected.has(tree)) continue; // Skip deselected trees
      this._createAndRegister(nodeId);
    }
  }

  /** Subscribe to node-unlocked so effects are created as the player earns XP during a run. */
  private _listenForUnlocks(): void {
    this.deps.player.events.on(
      'node-unlocked',
      ({ nodeId }: { nodeId: string }) => this._createAndRegister(nodeId),
    );
  }

  /** Diff attunement changes to unregister removed skill effects and re-register added ones. */
  private _listenForAttunementChanges(): void {
    this._previousAttuned = new Set(this.deps.player.attunement.attunedSkills);
    this.deps.player.events.on(
      'attunement-changed',
      ({ attunedSkills }: { attunedSkills: string[] }) => {
        const current = new Set(attunedSkills);
        // Unregister effects for skills removed from the hotbar
        for (const skillId of this._previousAttuned) {
          if (!current.has(skillId)) {
            this.deps.player.skillActivation.unregisterEffect(skillId);
          }
        }
        // Re-register effects for skills added to the hotbar (handles re-attune after unattune)
        for (const skillId of current) {
          if (!this._previousAttuned.has(skillId)) {
            this._createAndRegister(skillId);
          }
        }
        this._previousAttuned = current;
      },
    );
  }

  /** Unregister passive effects when a tree is deselected; re-register when re-selected. */
  private _listenForTreeSelectionChanges(): void {
    this._previousSelectedTrees = new Set(this.deps.player.attunement.getSelectedTrees());
    this.deps.player.events.on(
      'tree-selection-changed',
      ({ selectedTrees }: { selectedTrees: string[] }) => {
        const current = new Set(selectedTrees);
        // Find deselected trees — unregister all effects (passives) from them
        for (const treeName of this._previousSelectedTrees) {
          if (!current.has(treeName)) {
            for (const nodeId of this.registry.keys()) {
              if (ConfigLoader.getV4SkillTree(nodeId) === treeName) {
                this.deps.player.skillActivation.unregisterEffect(nodeId);
              }
            }
          }
        }
        // Find newly selected trees — re-register effects for unlocked nodes
        for (const treeName of current) {
          if (!this._previousSelectedTrees.has(treeName)) {
            for (const nodeId of this.registry.keys()) {
              if (ConfigLoader.getV4SkillTree(nodeId) === treeName) {
                this._createAndRegister(nodeId);
              }
            }
          }
        }
        this._previousSelectedTrees = current;
      },
    );
  }

  /**
   * Idempotent: creates a SkillEffect for the given node and registers it.
   * No-ops if the node has no registered factory or if its effect is already registered.
   */
  private _createAndRegister(nodeId: string): void {
    const factory = this.registry.get(nodeId);
    if (!factory) return; // Data-only passive — no SkillEffect class for this node
    if (this.deps.player.skillActivation.getEffect(nodeId)) return; // Already registered
    const effect = factory();
    if (effect) this.deps.player.skillActivation.registerEffect(effect);
  }
}
